"use client";

import { c as _c } from "react/compiler-runtime";
import { SquareArrowOutUpRight } from "lucide-react";
import Link from "next/link";
import { getTprDepartmentName } from "@/configuration/groups/tpr/athena";
import { resolveTprCasePolicyPackageId } from "@/configuration/groups/tpr/case-policies";
import { TprManualStepContinueButton } from "@/configuration/groups/tpr/components/manual-step-continue-button";
import { TprNetworkStatus } from "@/configuration/groups/tpr/components/network-status";
import { TprOpsContinueButton } from "@/configuration/groups/tpr/components/ops-continue-button";
import { TprReceivingAssignmentContinueButton } from "@/configuration/groups/tpr/components/receiving-assignment-continue-button";
import { TprReceivingAssignmentEditor } from "@/configuration/groups/tpr/components/receiving-assignment-editor";
import { TprSyncAuthButtons } from "@/configuration/groups/tpr/components/sync-auth-buttons";
import { refreshTprWorkflowProgress, useTprLiveWorkflowRefresh } from "@/configuration/groups/tpr/components/workflow-progress";
import { isTprSpecialCaseInsurance } from "@/configuration/groups/tpr/insurance-special-cases";
import { TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION } from "@/configuration/groups/tpr/referral-auth-instructions";
import type { TprDocumentUpload } from "@/configuration/groups/tpr/workflows/classifications";
import { getTprManualBypassStep, isTprContinuedFailure, isTprEligibilityTimeoutReview, isTprStoppedOnStep } from "@/configuration/groups/tpr/workflows/manual-step-continuation";
import { describeTprSpecialtyCategories, matchTprDiagnosisSpecialties } from "@/configuration/groups/tpr/workflows/physician-roster";
import { tprReferralAuthNeedsPendingUpload, tprReferralAuthNeedsReview } from "@/configuration/groups/tpr/workflows/referral-auth-status";
import type { StediEligibilityDisplayLabel, TprSyncChecklistTraceEntry, TprSyncChecklistUrgency } from "@/configuration/groups/tpr/workflows/sync-checklist";
import { RelationshipToSubscriber } from "@/packages/shared/src/models/coverage";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { AthenaCoverageEvidence } from "@/src/components/tasks/approval-types/athena-coverage-evidence";
import { AthenaCoverageResolutionDialog } from "@/src/components/tasks/athena-coverage-resolution-dialog";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { TaskSchedulingRecommendation } from "@/src/components/tasks/task-scheduling-recommendation";
import { TaskStepCard } from "@/src/components/tasks/task-step-card";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { useTaskStepVerifications, useApproveTaskMutation, useLocalTaskUpdateMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { findPatientContactSyncGaps, isCompleteAddress, PatientContactSyncGap } from "@/src/lib/ehr/patient-contact-sync";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { formatDateMmDdYyyy, formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import { errorFromStepOutput } from "@/src/lib/workflows/step-failure-output";
import type { ReceivingAssignmentOutput } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput, TprReferralAuthorizationOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { getFormattedAddress } from "@/src/models/address";
import type { Appointment } from "@/src/models/appointment";
import { Coverage, CoverageType, getCoverageDisplayName, getSubscriberName } from "@/src/models/coverage";
import { type DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { getPatientDisplayName, getPatientFaxNumber, getPatientName, getPatientPhoneNumber, type Patient } from "@/src/models/patient";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import type { ProviderInsuranceRequirements } from "@/src/models/provider-insurance-requirements";
import { Task, TaskStatus, TaskType } from "@/src/models/task";
import { ApprovalType, type TaskApproval } from "@/src/models/task-approval";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";
import { resolveTaskStepPhase, TaskStepPhase, type PinnableTaskStepPhase, type TaskStepCardState } from "@/src/models/task-step-card";
import { VerificationResult } from "@/src/models/task-verification";
const NOT_AVAILABLE = "N/A";
type TprCoverageSlot = {
  label: string;
  title: string;
};
function isTprCasePolicyCoverage(coverage: Coverage | null | undefined): boolean {
  return coverage?.coverageType === CoverageType.INSURANCE && resolveTprCasePolicyPackageId(coverage) !== null;
}
function getTprCoverageSlots(primary: Coverage | null | undefined, secondary: Coverage | null | undefined): {
  primary: TprCoverageSlot;
  secondary: TprCoverageSlot;
} {
  const primaryCasePolicy = isTprCasePolicyCoverage(primary);
  const secondaryCasePolicy = isTprCasePolicyCoverage(secondary);
  const casePolicySlot = (position: number): TprCoverageSlot => {
    const label = primaryCasePolicy && secondaryCasePolicy ? `Case Policy ${position}` : "Case Policy";
    return {
      label,
      title: label
    };
  };
  return {
    primary: primaryCasePolicy ? casePolicySlot(1) : {
      label: "Primary",
      title: "Primary Insurance"
    },
    secondary: secondaryCasePolicy ? casePolicySlot(2) : {
      label: "Secondary",
      title: "Secondary Insurance"
    }
  };
}

/**
 * Lowercased opening segment every TPR disposition note carries — see `getUnifiedReferralInternalNote`
 * in configuration/groups/tpr/workflows/referral-notes.ts. Matched anywhere in the note (not just at
 * the start) because Athena appends internal notes server-side, so ours can end up after an earlier
 * one. Used to tell "Athena already has our note" apart from "Athena has some other note" — e.g.
 * `careTeamStep`'s referring-provider address-mismatch note, which is written earlier and carries no
 * such segment.
 */
const REVIEWED_BY_VALERIE_HEALTH_MARKER = "reviewed by valerie health";

// Plain string literals rather than importing the `TprSyncBucket` enum's runtime values: this is a
// "use client" component, and `sync-checklist.ts` transitively pulls in server-only Athena/GCP client
// code, so any real (non-type-only) import from it breaks the browser bundle. Values must match
// `TprSyncBucket` in configuration/groups/tpr/workflows/sync-checklist.ts exactly.
const TPR_MANUAL_REVIEW_BUCKET = "biltmorecardreferral";

/**
 * Plain-English labels for the 3 real destinations ops actually routes to. `TPR_MANUAL_REVIEW_BUCKET`
 * is deliberately excluded — it's the decision tree's "unable to auto-determine" catch-all, not a 4th
 * destination, so it gets its own "no confident suggestion" message instead of a label here.
 */
const SUGGESTED_BUCKET_LABELS: Record<string, string> = {
  closed: "Closed — upcoming appointment already scheduled",
  sscardio: "SS Cardio — stays with practice",
  biltmorecardiosched: "Biltmore — ready to schedule"
};
const NO_CONFIDENT_BUCKET_SUGGESTION = "No confident suggestion — ops must choose SS Cardio / Biltmore (ready to schedule) / Closed based on the details above.";

/** Renders provider name / NPI as independently copyable fields (no address — that's its own line now). */
function ProviderCopyFields(t0) {
  const $ = _c(9);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = provider && getPractitionerNameWithCredentials(provider) || NOT_AVAILABLE;
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  const npi = provider?.npiNumber || NOT_AVAILABLE;
  let t2;
  if ($[2] !== name) {
    t2 = <CopyableContent text={name} />;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== npi) {
    t3 = <CopyableContent text={npi} />;
    $[4] = npi;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <>Name: {t2}, NPI: {t3}</>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function ResolvedProviderCopyFields(t0) {
  const $ = _c(8);
  const {
    providerLabel,
    providerNpi,
    writeSafe
  } = t0;
  const t1 = providerLabel || NOT_AVAILABLE;
  let t2;
  if ($[0] !== t1) {
    t2 = <CopyableContent text={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = providerNpi || NOT_AVAILABLE;
  let t4;
  if ($[2] !== t3) {
    t4 = <CopyableContent text={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !writeSafe && " \u2014 verify manually before entry";
  let t6;
  if ($[4] !== t2 || $[5] !== t4 || $[6] !== t5) {
    t6 = <>Name: {t2}, NPI:{" "}{t4}{t5}</>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function getCoverageSummary(coverage: Coverage | null | undefined): string | null {
  if (!coverage) return null;
  const display = getCoverageDisplayName({
    coverage,
    includesPlan: true,
    includeSubscriberName: true
  });
  return coverage.subscriberId ? `${display} (Member ID: ${coverage.subscriberId})` : display;
}

/**
 * Who the plan is actually held by. Ops keys the subscriber into Athena separately from the
 * patient, so a plan held by a spouse or parent has to be visible on the card rather than buried
 * in "More insurance detail".
 *
 * `relationshipToSubscriber` is the *patient's* relationship to the insured, so it is phrased as
 * "patient is Child" — "John Smith (Child)" reads as though John is the child. Self is never
 * shown: `coverageSchema` fills an uncaptured relationship in as Self, so it can't be told apart
 * from a real one, and a bare "Self" would tell ops the patient holds a plan nobody captured.
 */
function getPolicyHolder(coverage: Coverage | null | undefined): string | null {
  if (!coverage || coverage.coverageType === CoverageType.SELF_PAY) return null;
  const name = getSubscriberName(coverage);
  const relationship = coverage.relationshipToSubscriber && coverage.relationshipToSubscriber !== RelationshipToSubscriber.SELF ? `patient is ${coverage.relationshipToSubscriber}` : null;
  if (!name) return relationship ? `Name not captured (${relationship})` : null;
  return relationship ? `${name} (${relationship})` : name;
}

/** One line per past appointment for the "Patient Type" sanity-check list, most-recent-first. */
function formatAppointmentHistoryEntry(appointment: Appointment): string {
  const date = appointment.appointmentDatetime ? formatHumanReadableDate(appointment.appointmentDatetime) : "TBD";
  const type = formatFromUnderscoreToHumanReadable(appointment.type) || NOT_AVAILABLE;
  const status = formatFromUnderscoreToHumanReadable(appointment.status || "") || NOT_AVAILABLE;
  return `${date} - ${type} (${status})`;
}
function sortAppointmentsMostRecentFirst(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((a, b) => {
    const aTime = a.appointmentDatetime ? new Date(a.appointmentDatetime).getTime() : 0;
    const bTime = b.appointmentDatetime ? new Date(b.appointmentDatetime).getTime() : 0;
    return bTime - aTime;
  });
}
type DataSourceEntry = {
  label: string;
  value: string;
  source: "Referral" | "Patient Chart" | "N/A";
  missing: boolean;
};

/**
 * A field only counts as truly missing if it's absent from BOTH the referral's extracted data AND
 * the patient's existing Athena chart — an extraction miss isn't "missing" if the chart already has
 * it. Prefers the referral's value when both are present, since that's what's freshest off the fax.
 */
function resolveDataSourceEntry(label: string, referralValue: string | null, chartValue: string | null): DataSourceEntry {
  if (referralValue) return {
    label,
    value: referralValue,
    source: "Referral",
    missing: false
  };
  if (chartValue) return {
    label,
    value: chartValue,
    source: "Patient Chart",
    missing: false
  };
  return {
    label,
    value: NOT_AVAILABLE,
    source: "N/A",
    missing: true
  };
}
type TimelineStatus = TaskStepCardState;

/**
 * Stable identity for each TPR checklist step, used as `task_step_verification.step_key`
 * so recorded accuracy stays comparable when steps are renumbered, retitled, or reordered. Never
 * derive this from `number` or `title` — both change; these slugs must not.
 */
export const TPR_TASK_STEP_KEY = {
  LOCATE_DOCUMENT: "locate_document",
  PROVIDER_LOCATION: "provider_location",
  DEMOGRAPHICS_CARE_TEAM: "demographics_care_team",
  INSURANCE: "insurance",
  REFERRAL_AUTHORIZATION: "referral_authorization",
  OUTBOUND_FAXES: "outbound_faxes",
  FULL_REGISTRATION: "full_registration",
  PROCESS_REFERRAL_DOCUMENT: "process_referral_document",
  DOCUMENT_UPLOAD: "document_upload",
  QUICKVIEW: "quickview",
  BUCKET_ASSIGNMENT: "bucket_assignment"
} as const;
export type TprTaskStepKey = (typeof TPR_TASK_STEP_KEY)[keyof typeof TPR_TASK_STEP_KEY];
type TimelineStep = {
  number: number;
  title: string;
  stepKey: TprTaskStepKey;
  status: TimelineStatus;
  /** See `TPR_PINNED_STEP_PHASE`. Omitted means the phase follows the status. */
  phase?: PinnableTaskStepPhase;
  /** Why this step needs a human, in place of the card's generic instruction. */
  detail?: string;
  summary: string;
  workflowStepKeys: StepKey[];
  /**
   * The few fields worth seeing on a card that is still waiting its turn — what automation
   * decided, without the evidence behind it. Items with no value are dropped, so a step the
   * workflow hasn't reached shows nothing rather than a row of N/A.
   */
  preview?: DecisionMetadataItem[];
  content?: React.ReactNode;
  continuation?: {
    instruction: string;
    control?: React.ReactNode;
  };
};

/**
 * Steps whose phase is a property of the step itself, not of how cleanly automation happened to
 * resolve it on this referral.
 *
 * These are the TPR steps automation was never built to finish: outbound faxes are still
 * partially manual, the referral document needs a human to key parts of it into Athena, and the
 * bucket is ops' judgement call. They stay `ACTION_NEEDED` even when every workflow step under
 * them completed, because "completed" there only means the automated part is done — the human
 * part still isn't.
 *
 * Referral Authorization is pinned per-referral rather than here: it's `ACTION_NEEDED` only when
 * it needs an outbound fax, and plain `QA` when adding the authorization object is the whole job.
 *
 * Every other step is automated end to end, so it falls through to its status: `QA` when it
 * worked, `FAILED` when it didn't. A failed sync outranks a pin — see `resolveTaskStepPhase`.
 */
const TPR_PINNED_STEP_PHASE: Partial<Record<TprTaskStepKey, PinnableTaskStepPhase>> = {
  [TPR_TASK_STEP_KEY.OUTBOUND_FAXES]: TaskStepPhase.ACTION_NEEDED,
  [TPR_TASK_STEP_KEY.PROCESS_REFERRAL_DOCUMENT]: TaskStepPhase.ACTION_NEEDED,
  [TPR_TASK_STEP_KEY.BUCKET_ASSIGNMENT]: TaskStepPhase.ACTION_NEEDED
};
export type DecisionMetadataItem = {
  label: string;
  value: React.ReactNode;
};
export type TprChartReferralAuth = {
  referralAuthId: string;
  number: string | null;
  startDate: string | null;
  endDate: string | null;
  expired: boolean | null;
};
export type TprChartRequestLetter = {
  documentEhrId: string;
  documentDate: string | null;
  description: string | null;
};
export type TprChartDocument = {
  documentEhrId: string;
  name: string;
  documentDate: string | null;
};
type TprOpsCrossCheck = {
  patientName: string;
  patientDateOfBirth: string;
  patientHomePhoneNumber: string;
  patientMobilePhoneNumber: string;
  patientWorkPhoneNumber: string;
  patientEmail: string;
  patientFaxNumber: string;
  patientEthnicity: string;
  patientRace: string;
  patientLanguage: string;
  patientHomeAddress: string;
  receivingProviderName: string;
  receivingProviderNpi: string;
  referringProviderName: string;
  referringProviderNpi: string;
  referringProviderSpecialty: string;
  referringLocationName: string;
  referringLocationAddress: string;
  emergencyContactName: string;
  emergencyContactPhoneNumber: string;
  emergencyContactRelationship: string;
  dateReceived: string;
  primaryPlanLabel: string;
  primarySubscriberId: string;
  primaryPolicyHolder: string;
  /** Null when the patient genuinely has no secondary plan, as opposed to an uncaptured one. */
  secondaryPlanLabel: string | null;
  secondarySubscriberId: string | null;
  secondaryPolicyHolder: string | null;
  isSecondarySpecialCaseInsurance: boolean;
  /**
   * The authorization terms as extracted from the referral. Ops needs the whole set — a number with
   * no validity window or visit count can't be checked against what Athena holds.
   */
  authorizationNumber: string;
  authorizationStatus: string;
  authorizationStartDate: string;
  authorizationEndDate: string;
  authorizationVisits: string;
  /** Separate from `authorizationVisits`: "services" and "visits" are distinct extracted fields. */
  authorizationServices: string | null;
  /** PIMC / Triwest / MercyCare — special-case payers ops wants visually flagged on Insurance. */
  isSpecialCaseInsurance: boolean;
  primaryCoverageSlot: TprCoverageSlot;
  secondaryCoverageSlot: TprCoverageSlot;
  diagnosis: string;
  /** Which of TPR's four sub-specialty categories the diagnosis text maps to (General /
   * Interventional / Structural / EP) — the bucket the provider resolver matched providers against. */
  diagnosisCategories: string;
};

/**
 * The condensed body of a card that hasn't come up yet.
 *
 * Drops items with no value, so a step the workflow genuinely hasn't reached renders nothing
 * instead of a row of N/A — the point is to show what was decided, and nothing has been yet.
 */
function TimelineStepPreview(t0) {
  const $ = _c(6);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items ?? [];
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = t1.filter(_temp);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const resolved = t2;
  let t3;
  if ($[4] !== resolved) {
    t3 = resolved.length > 0 ? <DecisionMetadata items={resolved} /> : null;
    $[4] = resolved;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp(item) {
  return item.value && item.value !== NOT_AVAILABLE;
}
export function DecisionMetadata(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp2);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="flex flex-wrap gap-2">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(item) {
  return <div key={item.label} className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"><dt className="text-muted-foreground">{item.label}</dt><dd className="font-medium text-foreground">{item.value}</dd></div>;
}
type ProviderSelectionEvidence = NonNullable<ReceivingAssignmentOutput["providerSelectionEvidence"]>;
type ProviderSelectionStep = NonNullable<ProviderSelectionEvidence["selectedStep"]>;
function providerSelectionReason(selectedStep: ProviderSelectionEvidence["selectedStep"], internalReferral = false): string {
  switch (selectedStep) {
    case "upcoming_appointment":
      return "Selected from the earliest qualifying Biltmore appointment within 60 days.";
    case "past_appointment":
      return "Selected from the most recent qualifying completed Biltmore appointment in the past three years.";
    case "usual_provider":
      return "Legacy selection used the Athena usual provider; verify this assignment against appointment history.";
    case "referral_provider":
      return internalReferral ? "Selected because this is an internal Biltmore-to-Biltmore referral, so the provider named on the referral takes precedence over appointment history." : "Selected because this provider is named on the referral.";
    case "diagnosis_match":
      return "Selected because this provider treats the referral diagnosis at the chosen location.";
    default:
      return "No provider was selected automatically.";
  }
}
function ProviderSelectionChecklist(t0) {
  const $ = _c(37);
  const {
    evidence,
    upcomingAppointmentProviderSummary,
    referralProviderName,
    referralProviderNpi,
    diagnosis,
    diagnosisCategories,
    selectedProviderName,
    selectedLocation
  } = t0;
  let t1;
  if ($[0] !== evidence.appointmentProviders) {
    t1 = evidence.appointmentProviders.some(_temp3);
    $[0] = evidence.appointmentProviders;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasQualificationEvidence = t1;
  let t2;
  if ($[2] !== evidence.appointmentProviders || $[3] !== evidence.selectedStep || $[4] !== hasQualificationEvidence || $[5] !== upcomingAppointmentProviderSummary) {
    t2 = period => {
      const providers = evidence.appointmentProviders.flatMap(provider_0 => {
        const count = period === "past" ? provider_0.qualifyingPastAppointmentCount ?? 0 : provider_0.qualifyingUpcomingAppointmentCount ?? 0;
        const locationEhrId = period === "past" ? provider_0.mostRecentPastLocationEhrId : provider_0.earliestUpcomingLocationEhrId;
        const location = locationEhrId ? getTprDepartmentName(locationEhrId) ?? `Athena department ${locationEhrId}` : null;
        return count > 0 ? [`${provider_0.providerName}${provider_0.providerNpi ? ` · ${provider_0.providerNpi}` : ""}${location ? ` · ${location}` : ""} (${count})`] : [];
      });
      if (providers.length > 0) {
        return providers.join(", ");
      }
      if (period === "upcoming" && !hasQualificationEvidence && upcomingAppointmentProviderSummary) {
        return upcomingAppointmentProviderSummary;
      }
      if (!hasQualificationEvidence && (evidence.selectedStep === "usual_provider" || evidence.appointmentProviders.length > 0)) {
        return "Not captured for this legacy task";
      }
      return "None";
    };
    $[2] = evidence.appointmentProviders;
    $[3] = evidence.selectedStep;
    $[4] = hasQualificationEvidence;
    $[5] = upcomingAppointmentProviderSummary;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const providerCountSummary = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[7] !== diagnosis || $[8] !== diagnosisCategories || $[9] !== evidence.internalReferral || $[10] !== evidence.pastAppointmentCount || $[11] !== evidence.selectedStep || $[12] !== evidence.upcomingAppointmentCount || $[13] !== providerCountSummary || $[14] !== referralProviderName || $[15] !== referralProviderNpi || $[16] !== selectedLocation || $[17] !== selectedProviderName) {
    const upcomingAppointmentStep = {
      key: "upcoming_appointment" as const,
      title: "Check for an upcoming appointment",
      items: [{
        label: "Upcoming within 60 days",
        value: evidence.upcomingAppointmentCount
      }, {
        label: "Qualifying appointments at Biltmore locations",
        value: providerCountSummary("upcoming")
      }]
    };
    const pastAppointmentStep = {
      key: "past_appointment" as const,
      title: "Check past appointments at Biltmore locations",
      items: [{
        label: "Completed in past 3 years",
        value: evidence.pastAppointmentCount
      }, {
        label: "Qualifying appointments at Biltmore locations",
        value: providerCountSummary("past")
      }]
    };
    const referralProviderStep = {
      key: "referral_provider" as const,
      title: "Use the provider named on the referral",
      items: [{
        label: "Provider",
        value: referralProviderName
      }, {
        label: "NPI",
        value: referralProviderNpi
      }]
    };
    const diagnosisMatchStep = {
      key: "diagnosis_match" as const,
      title: "Sanity-check the pick or find a replacement",
      items: [{
        label: "Diagnosis",
        value: diagnosis
      }, {
        label: "Diagnosis category",
        value: diagnosisCategories
      }, {
        label: "Location",
        value: selectedLocation
      }, {
        label: "Final provider",
        value: selectedProviderName
      }]
    };
    const steps = evidence.internalReferral ? [referralProviderStep, upcomingAppointmentStep, pastAppointmentStep, diagnosisMatchStep] : [upcomingAppointmentStep, pastAppointmentStep, referralProviderStep, diagnosisMatchStep];
    t6 = "space-y-2 border-t pt-3";
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <summary className="cursor-pointer text-sm font-medium">Find the Provider and Location</summary>;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    t5 = "mt-2 space-y-2";
    t3 = "space-y-2";
    t4 = steps.map((step, index) => {
      const selected = evidence.selectedStep === step.key;
      return <li key={step.key} className={selected ? "space-y-2 rounded-md border border-foreground/40 bg-muted p-3" : "space-y-2 rounded-md border bg-muted/10 p-3"}><div className="flex items-center justify-between gap-3"><span className="text-sm font-medium">Step {index + 1} — {step.title}</span>{selected ? <Badge variant="outline" className="border-foreground/40 bg-muted font-medium text-foreground">Selected</Badge> : null}</div><DecisionMetadata items={selected ? [...step.items, {
          label: "Picked provider",
          value: selectedProviderName
        }, {
          label: "Reason",
          value: providerSelectionReason(evidence.selectedStep, evidence.internalReferral)
        }] : step.items} /></li>;
    });
    $[7] = diagnosis;
    $[8] = diagnosisCategories;
    $[9] = evidence.internalReferral;
    $[10] = evidence.pastAppointmentCount;
    $[11] = evidence.selectedStep;
    $[12] = evidence.upcomingAppointmentCount;
    $[13] = providerCountSummary;
    $[14] = referralProviderName;
    $[15] = referralProviderNpi;
    $[16] = selectedLocation;
    $[17] = selectedProviderName;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
  }
  let t8;
  if ($[24] !== t3 || $[25] !== t4) {
    t8 = <ol className={t3}>{t4}</ol>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== evidence.selectedStep) {
    t9 = evidence.selectedStep === "usual_provider" ? <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">This assignment was created with the retired Athena usual-provider rule. Verify the provider and location against qualifying appointment history before following it.</div> : null;
    $[27] = evidence.selectedStep;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t5 || $[30] !== t8 || $[31] !== t9) {
    t10 = <div className={t5}>{t8}{t9}</div>;
    $[29] = t5;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t6 || $[35] !== t7) {
    t11 = <details className={t6}>{t7}{t10}</details>;
    $[33] = t10;
    $[34] = t6;
    $[35] = t7;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}
function _temp3(provider) {
  return provider.qualifyingUpcomingAppointmentCount !== undefined || provider.qualifyingPastAppointmentCount !== undefined;
}
function taskStepStatus(taskSteps: TaskStep[], keys: StepKey[]): TimelineStatus {
  const rows = keys.map(key => taskSteps.find(step => step.stepKey === key)).filter(Boolean);
  if (rows.some(row => row?.status === TaskStepStatus.FAILED)) return "failed";
  if (rows.some(row => row?.status === TaskStepStatus.ASYNC || row?.status === TaskStepStatus.PAUSED)) {
    return "waiting";
  }
  if (keys.every(key => taskSteps.some(step => step.stepKey === key && step.status === TaskStepStatus.COMPLETED))) {
    return "completed";
  }
  return "waiting";
}
function TimelineStepCard(t0) {
  const $ = _c(32);
  const {
    task,
    step,
    failedStepKey,
    bypassStepKey,
    failureReason,
    continuedPastFailure,
    signOffOnContinue,
    blockedBy,
    awaitingStep
  } = t0;
  const effectiveStatus = failedStepKey ? "failed" : awaitingStep ? "not_run" : step.status;
  const approvalMutation = useApproveTaskMutation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      invalidateOnSettled: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const localTaskUpdateMutation = useLocalTaskUpdateMutation(t1);
  const invalidateInstructions = useInvalidateServerComponent("getReferralInstructions");
  let t2;
  if ($[1] !== continuedPastFailure || $[2] !== failedStepKey || $[3] !== failureReason || $[4] !== signOffOnContinue || $[5] !== step || $[6] !== task) {
    t2 = failedStepKey ? {
      instruction: failedStepKey === StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY ? "The eligibility check failed. Verify or correct the coverage in Athena, then recheck eligibility to save the result before continuing." : continuedPastFailure ? `The ${formatFromUnderscoreToHumanReadable(failedStepKey).toLowerCase()} step failed${failureReason ? ` (${failureReason})` : ""}. The rest of the workflow ran anyway. Complete this step in Athena, then continue.` : `The ${formatFromUnderscoreToHumanReadable(failedStepKey).toLowerCase()} step failed${failureReason ? ` (${failureReason})` : ""}. Complete it manually in Athena, then continue with the remaining steps.`,
      control: <TprManualStepContinueButton task={task} stepKey={failedStepKey} signOff={signOffOnContinue ? step : undefined} />
    } : step.continuation;
    $[1] = continuedPastFailure;
    $[2] = failedStepKey;
    $[3] = failureReason;
    $[4] = signOffOnContinue;
    $[5] = step;
    $[6] = task;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const continuation = t2;
  let t3;
  if ($[8] !== approvalMutation || $[9] !== invalidateInstructions || $[10] !== localTaskUpdateMutation || $[11] !== task) {
    t3 = async () => {
      await approvalMutation.mutateAsync({
        taskId: task.id,
        approved: true
      });
      localTaskUpdateMutation.mutate({
        ...task,
        status: TaskStatus.PROCESSING
      });
      refreshTprWorkflowProgress(invalidateInstructions);
    };
    $[8] = approvalMutation;
    $[9] = invalidateInstructions;
    $[10] = localTaskUpdateMutation;
    $[11] = task;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleVerificationComplete = t3;
  const t4 = `${step.number}. ${step.title}`;
  const t5 = blockedBy ? `Waiting on step ${blockedBy.number} (${blockedBy.title}), which failed.` : !continuation?.control && continuation?.instruction || step.detail;
  const t6 = step.continuation ? handleVerificationComplete : undefined;
  let t7;
  if ($[13] !== bypassStepKey || $[14] !== continuation || $[15] !== task) {
    t7 = continuation?.control || bypassStepKey ? <div className="space-y-3">{continuation?.instruction ? <p className="text-sm text-muted-foreground">{continuation.instruction}</p> : null}{continuation?.control ? <div className="flex justify-center">{continuation.control}</div> : null}{bypassStepKey ? <div className="space-y-2"><p className="text-sm text-muted-foreground">Continue past this check without marking it verified. Your override will be recorded.</p><TprManualStepContinueButton task={task} stepKey={bypassStepKey} bypass={true} /></div> : null}</div> : undefined;
    $[13] = bypassStepKey;
    $[14] = continuation;
    $[15] = task;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== awaitingStep || $[18] !== step.content || $[19] !== step.preview) {
    t8 = awaitingStep ? <TimelineStepPreview items={step.preview} /> : step.content;
    $[17] = awaitingStep;
    $[18] = step.content;
    $[19] = step.preview;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== effectiveStatus || $[22] !== step.phase || $[23] !== step.stepKey || $[24] !== step.workflowStepKeys || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== task.id) {
    t9 = <TaskStepCard header={t4} status={effectiveStatus} phase={step.phase} detail={t5} taskId={task.id} stepKey={step.stepKey} workflowStepKeys={step.workflowStepKeys} onVerificationComplete={t6} action={t7}>{t8}</TaskStepCard>;
    $[21] = effectiveStatus;
    $[22] = step.phase;
    $[23] = step.stepKey;
    $[24] = step.workflowStepKeys;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = task.id;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
}
function TprStediEligibilityValues(t0) {
  const $ = _c(5);
  const {
    referralId
  } = t0;
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referralId);
  let t1;
  if ($[0] !== eligibilityCheck) {
    t1 = <StediEligibilitySummary check={eligibilityCheck} showHeader={false} />;
    $[0] = eligibilityCheck;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-sm text-muted-foreground">Coverage rechecks and edits are available on the Information tab.</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="space-y-3">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function TprChartReferralAuthDocuments(t0) {
  const $ = _c(7);
  const {
    documents
  } = t0;
  const t1 = documents ? ` (${documents.length})` : "";
  let t2;
  if ($[0] !== t1) {
    t2 = <summary className="cursor-pointer font-medium">Possible associated documents{t1}</summary>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== documents) {
    t3 = <div className="mt-2">{documents === null ? <p className="text-muted-foreground">Could not load the Athena chart documents. Check the chart directly.</p> : documents.length === 0 ? <p className="text-muted-foreground">No referral or authorization documents on the Athena chart.</p> : <ul className="list-disc space-y-1 pl-5">{documents.map(_temp4)}</ul>}</div>;
    $[2] = documents;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <details className="rounded-md border p-3 text-sm">{t2}{t3}</details>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp4(document) {
  return <li key={document.documentEhrId}>{document.name} · <CopyableContent text={document.documentEhrId} textClassName="text-xs" />{document.documentDate ? ` · ${document.documentDate}` : ""}</li>;
}
function TprSyncTimeline(t0) {
  const $ = _c(103);
  const {
    task,
    referralId,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    athenaCoverageLive,
    insuranceRequirements,
    referralAuthorization,
    resolvedReceiving,
    patientLabel,
    patientEhrId,
    documentId,
    bucketLabel,
    internalNoteToEnter,
    urgency,
    actionNote,
    decisionTrace,
    insuranceStatusDisplay,
    hasMissingInformation,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    upcomingAppointments,
    pastAppointments,
    documentUploads,
    privacyNoticeOnFile,
    referralSourceOnFile,
    isPrimaryCareProvider,
    referringProviderMarkedPcp,
    primaryCareProviderAlreadyPresent,
    patientDisplayId,
    opsCrossCheck,
    patientContactSyncGaps,
    chartReferralAuths,
    chartRequestLetters,
    isReferralAuth,
    chartReferralAuthDocuments
  } = t0;
  useTprLiveWorkflowRefresh(useInvalidateServerComponent("getReferralInstructions"));
  const {
    verdictByStepKey
  } = useTaskStepVerifications(task.id);
  let t1;
  if ($[0] !== verdictByStepKey) {
    t1 = verdictByStepKey.get(TPR_TASK_STEP_KEY.PROVIDER_LOCATION)?.verdict;
    $[0] = verdictByStepKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const providerAssignmentMarkedIncorrect = t1 === VerificationResult.INCORRECT;
  let t2;
  if ($[2] !== taskSteps) {
    t2 = taskSteps.some(_temp5);
    $[2] = taskSteps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isProviderFirst = t2;
  let t3;
  if ($[4] !== pastAppointments || $[5] !== resolvedReceiving.providerSelectionEvidence || $[6] !== upcomingAppointments) {
    t3 = resolvedReceiving.providerSelectionEvidence ?? {
      selectedStep: null,
      upcomingAppointmentCount: upcomingAppointments.length,
      pastAppointmentCount: pastAppointments.length,
      internalReferral: false,
      appointmentProviders: []
    };
    $[4] = pastAppointments;
    $[5] = resolvedReceiving.providerSelectionEvidence;
    $[6] = upcomingAppointments;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const providerSelectionEvidence = t3;
  const workflowStillProcessing = task.status === TaskStatus.PROCESSING;
  let t4;
  if ($[8] !== actionNote || $[9] !== athenaCoverageLive.primary || $[10] !== athenaCoverageLive.secondary || $[11] !== bucketLabel || $[12] !== chartReferralAuthDocuments || $[13] !== chartReferralAuths || $[14] !== chartRequestLetters || $[15] !== coverageReadback || $[16] !== decisionTrace || $[17] !== documentId || $[18] !== documentUploads || $[19] !== followThroughEligible || $[20] !== hasMissingInformation || $[21] !== insuranceRequirements || $[22] !== insuranceStatusDisplay || $[23] !== internalNoteToEnter || $[24] !== isPrimaryCareProvider || $[25] !== isProviderFirst || $[26] !== isReferralAuth || $[27] !== opsCrossCheck.authorizationEndDate || $[28] !== opsCrossCheck.authorizationNumber || $[29] !== opsCrossCheck.authorizationServices || $[30] !== opsCrossCheck.authorizationStartDate || $[31] !== opsCrossCheck.authorizationStatus || $[32] !== opsCrossCheck.authorizationVisits || $[33] !== opsCrossCheck.dateReceived || $[34] !== opsCrossCheck.diagnosis || $[35] !== opsCrossCheck.diagnosisCategories || $[36] !== opsCrossCheck.emergencyContactName || $[37] !== opsCrossCheck.emergencyContactPhoneNumber || $[38] !== opsCrossCheck.emergencyContactRelationship || $[39] !== opsCrossCheck.isSecondarySpecialCaseInsurance || $[40] !== opsCrossCheck.isSpecialCaseInsurance || $[41] !== opsCrossCheck.patientDateOfBirth || $[42] !== opsCrossCheck.patientEmail || $[43] !== opsCrossCheck.patientEthnicity || $[44] !== opsCrossCheck.patientFaxNumber || $[45] !== opsCrossCheck.patientHomeAddress || $[46] !== opsCrossCheck.patientHomePhoneNumber || $[47] !== opsCrossCheck.patientLanguage || $[48] !== opsCrossCheck.patientMobilePhoneNumber || $[49] !== opsCrossCheck.patientName || $[50] !== opsCrossCheck.patientRace || $[51] !== opsCrossCheck.patientWorkPhoneNumber || $[52] !== opsCrossCheck.primaryCoverageSlot.title || $[53] !== opsCrossCheck.primaryPlanLabel || $[54] !== opsCrossCheck.primaryPolicyHolder || $[55] !== opsCrossCheck.primarySubscriberId || $[56] !== opsCrossCheck.receivingProviderName || $[57] !== opsCrossCheck.receivingProviderNpi || $[58] !== opsCrossCheck.referringLocationAddress || $[59] !== opsCrossCheck.referringProviderName || $[60] !== opsCrossCheck.referringProviderNpi || $[61] !== opsCrossCheck.referringProviderSpecialty || $[62] !== opsCrossCheck.secondaryCoverageSlot.title || $[63] !== opsCrossCheck.secondaryPlanLabel || $[64] !== opsCrossCheck.secondaryPolicyHolder || $[65] !== opsCrossCheck.secondarySubscriberId || $[66] !== patientContactSyncGaps || $[67] !== patientDisplayId || $[68] !== patientEhrId || $[69] !== patientLabel || $[70] !== pendingApproval || $[71] !== primaryCareProviderAlreadyPresent || $[72] !== privacyNoticeOnFile || $[73] !== providerAssignmentMarkedIncorrect || $[74] !== providerSelectionEvidence || $[75] !== receivingAssignmentConfirmed || $[76] !== referralAuthorization || $[77] !== referralId || $[78] !== referralSourceOnFile || $[79] !== referringProviderMarkedPcp || $[80] !== resolvedReceiving.advisories || $[81] !== resolvedReceiving.flags || $[82] !== resolvedReceiving.locationEhrId || $[83] !== resolvedReceiving.locationLabel || $[84] !== resolvedReceiving.providerLabel || $[85] !== resolvedReceiving.providerNpi || $[86] !== resolvedReceiving.providerWriteSafe || $[87] !== resolvedReceiving.routedToSscardio || $[88] !== resolvedReceiving.usualProviderWriteSafe || $[89] !== task || $[90] !== taskSteps || $[91] !== unresolvedUpcomingProviders || $[92] !== upcomingAppointmentProviderSummary || $[93] !== urgency.isUrgent || $[94] !== urgency.reason || $[95] !== urgency.schedulingPriority || $[96] !== verdictByStepKey || $[97] !== workflowStillProcessing) {
    const assignmentStatus = isProviderFirst ? taskStepStatus(taskSteps, [StepKey.RESOLVE_RECEIVING_ASSIGNMENT]) : workflowStillProcessing ? "waiting" : "not_run";
    const assignmentDependentStatus = assignmentStatus === "failed" ? "not_run" : assignmentStatus;
    const receivingAssignmentApproval = pendingApproval?.approvalType === ApprovalType.TPR_RECEIVING_ASSIGNMENT ? pendingApproval : null;
    const assignmentRequiresManualReview = assignmentStatus === "completed" && !resolvedReceiving.providerWriteSafe && receivingAssignmentApproval !== null;
    const usesRetiredUsualProviderRule = providerSelectionEvidence.selectedStep === "usual_provider";
    const assignmentNeedsManualConfirmation = !resolvedReceiving.providerWriteSafe || usesRetiredUsualProviderRule;
    const assignmentWasManuallyConfirmed = assignmentStatus === "completed" && assignmentNeedsManualConfirmation && receivingAssignmentConfirmed;
    const assignmentReviewStatus = assignmentWasManuallyConfirmed ? "completed" : assignmentRequiresManualReview || usesRetiredUsualProviderRule ? "verification_required" : assignmentDependentStatus;
    const assignmentNeedsReview = !assignmentWasManuallyConfirmed && (assignmentRequiresManualReview || usesRetiredUsualProviderRule);
    const gateStatus = taskStepStatus(taskSteps, [StepKey.TPR_CAMELBACK_FOLLOW_THROUGH]);
    const assignmentSelectionIncomplete = !resolvedReceiving.providerLabel?.trim() || !resolvedReceiving.providerNpi?.trim() || !resolvedReceiving.locationLabel?.trim();
    const assignmentResolved = assignmentStatus === "completed" || assignmentStatus === "failed";
    const assignmentConfirmationStatus = assignmentResolved && assignmentSelectionIncomplete ? "failed" : assignmentWasManuallyConfirmed ? "completed" : assignmentReviewStatus === "verification_required" ? "verification_required" : assignmentStatus === "completed" ? gateStatus : assignmentStatus;
    const providerAssignmentEditable = providerAssignmentMarkedIncorrect || assignmentConfirmationStatus === "failed";
    const confirmedNonCamelbackAssignment = assignmentStatus === "completed" && !followThroughEligible && receivingAssignmentApproval === null;
    const skippedFollowThroughStatus = confirmedNonCamelbackAssignment ? "not_needed" : "not_run";
    const skippedFollowThroughSummary = assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : confirmedNonCamelbackAssignment ? "Not needed for the resolved non-Camelback receiving assignment." : "Not run because the receiving assignment has not completed.";
    const coverageApproval = pendingApproval?.approvalType === ApprovalType.ATHENA_COVERAGE ? pendingApproval : null;
    const eligibilityTimedOut = taskSteps.some(step_0 => isTprEligibilityTimeoutReview(step_0) && isTprStoppedOnStep(task.status, taskSteps, step_0));
    const opsApproval = pendingApproval?.approvalType === ApprovalType.TPR_REFERRAL_OPS ? pendingApproval : null;
    const opsKind = opsApproval?.metadata?.kind ?? null;
    const manualBypassStepKey = getTprManualBypassStep(task.status, taskSteps, pendingApproval)?.stepKey as StepKey | undefined;
    const waitingForAssociatedReferral = taskSteps.some(_temp6);
    const coverageReadbackComplete = taskSteps.some(_temp7);
    const authRequestUploaded = taskSteps.some(_temp8);
    const notesComplete = taskSteps.some(_temp9);
    const missingInfoUploadComplete = taskSteps.some(_temp10);
    const followThroughStatus = keys => followThroughEligible ? taskStepStatus(taskSteps, keys) : skippedFollowThroughStatus;
    const resolveConsolidatedStatus = _temp11;
    const livePrimaryAthenaPlan = athenaCoverageLive.primary && getCoverageDisplayName({
      coverage: athenaCoverageLive.primary
    }) || coverageReadback?.primary.planName || NOT_AVAILABLE;
    const liveSecondaryAthenaPlan = athenaCoverageLive.secondary && getCoverageDisplayName({
      coverage: athenaCoverageLive.secondary
    }) || coverageReadback?.secondary?.planName || NOT_AVAILABLE;
    const athenaCoverageDrifted = Boolean(coverageReadback && athenaCoverageLive.primary?.ehrId && athenaCoverageLive.primary.ehrId !== coverageReadback.primary.insuranceId);
    const primaryCoverageInAthena = Boolean(athenaCoverageLive.primary?.ehrId ?? coverageReadback?.primary.insuranceId);
    const insuranceStatus = primaryCoverageInAthena ? "completed" : workflowStillProcessing ? "waiting" : "failed";
    const authorizationStatus = opsKind === "authorization_review" ? "verification_required" : opsKind === "manual_authorization" ? "review_needed" : tprReferralAuthNeedsReview(referralAuthorization, insuranceRequirements) ? "review_needed" : referralAuthorization?.outcome === "not_needed" ? "not_needed" : referralAuthorization?.outcome === "needs_pending_request" ? "completed" : followThroughStatus([StepKey.TPR_REFERRAL_AUTHORIZATION]);
    const referralAuthNeedsPendingUpload = tprReferralAuthNeedsPendingUpload(referralAuthorization);
    const referralAuthNeedsOutboundFax = Boolean(insuranceRequirements?.referralMustBeFromPcp) || referralAuthNeedsPendingUpload;
    const requirementApplies = _temp12;
    const showPcpReferralRequirement = Boolean(insuranceRequirements?.referralMustBeFromPcp);
    const showAuthorizationRequirement = requirementApplies(insuranceRequirements?.authorizationRequirement);
    const showReferralRequirement = showAuthorizationRequirement || requirementApplies(insuranceRequirements?.referralRequirement);
    const createdChartReferralAuthId = referralAuthorization?.outcome === "created" ? referralAuthorization.referralAuthId : null;
    const documentAuthorizationNumber = opsCrossCheck.authorizationNumber === NOT_AVAILABLE ? null : opsCrossCheck.authorizationNumber.toLowerCase();
    const documentChartReferralAuths = chartReferralAuths.filter(authorization => authorization.referralAuthId === createdChartReferralAuthId || documentAuthorizationNumber !== null && authorization.number?.trim().toLowerCase() === documentAuthorizationNumber);
    const authorizationDetails = <div className="space-y-3"><DecisionMetadata items={[{
        label: "Auth number",
        value: <CopyableContent text={opsCrossCheck.authorizationNumber} textClassName="text-xs" />
      }, {
        label: "Status",
        value: opsCrossCheck.authorizationStatus
      }, {
        label: "Start date",
        value: opsCrossCheck.authorizationStartDate
      }, {
        label: "End date",
        value: opsCrossCheck.authorizationEndDate
      }, {
        label: "Visits",
        value: opsCrossCheck.authorizationVisits
      }, ...(opsCrossCheck.authorizationServices ? [{
        label: "Services",
        value: opsCrossCheck.authorizationServices
      }] : []), {
        label: "Outcome",
        value: referralAuthorization ? formatFromUnderscoreToHumanReadable(referralAuthorization.outcome) : "Pending"
      }, ...(referralAuthorization?.outcome === "created" ? [{
        label: "Athena object",
        value: <CopyableContent text={referralAuthorization.referralAuthId} textClassName="text-xs" />
      }, {
        label: "Appointment",
        value: referralAuthorization.needsManualAppointmentAttach ? "Attach manually" : referralAuthorization.appointmentAttached ? "Attached" : "None in window"
      }] : [])]} />{documentChartReferralAuths.length ? <details className="text-sm"><summary className="cursor-pointer font-medium">On the Athena chart</summary><div className="mt-2 space-y-2"><ul className="list-disc space-y-1 pl-5">{documentChartReferralAuths.map(_temp13)}</ul>{chartRequestLetters.length ? <div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request letters</p><ul className="list-disc space-y-1 pl-5">{chartRequestLetters.map(_temp14)}</ul></div> : null}</div></details> : null}<TprSyncAuthButtons canUploadLetter={referralAuthNeedsPendingUpload} enabled={Boolean(task.referral && patientEhrId !== NOT_AVAILABLE)} taskId={task.id} /></div>;
    const pcpFaxStatus = opsKind === "pcp_referral_fax" ? "review_needed" : insuranceRequirements && !insuranceRequirements.referralMustBeFromPcp ? "not_needed" : followThroughStatus([StepKey.TPR_PCP_REFERRAL_FAX]);
    const authRequestUploadStatus = opsKind === "auth_request_upload" ? "review_needed" : referralAuthNeedsPendingUpload ? authRequestUploaded ? "completed" : followThroughStatus([StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD]) : "not_needed";
    const missingInfoFaxStatus = !hasMissingInformation ? "not_needed" : missingInfoUploadComplete ? "review_needed" : followThroughStatus([StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX]);
    const outboundFaxStatus = resolveConsolidatedStatus([authRequestUploadStatus, missingInfoFaxStatus]);
    const resultDocumentsToLabel = documentUploads.filter(_temp15);
    const closeDocumentStatus = opsKind === "close_document" ? "review_needed" : followThroughStatus([StepKey.TPR_CLOSE_DOCUMENT]);
    const workflowNote = task.files.find(_temp16)?.note;
    const ensurePatientStatus = taskStepStatus(taskSteps, [StepKey.ENSURE_PATIENT]);
    const patientChartStatus = !patientEhrId || patientEhrId === NOT_AVAILABLE ? "review_needed" : "verification_required";
    const fullRegistrationReviewReasons = [privacyNoticeOnFile === false || workflowNote?.includes("Athena privacy notice could not be set automatically") ? "Set Athena Privacy to YES manually." : null, ensurePatientStatus === "completed" && referralSourceOnFile === null ? "Set \"How did you hear about us\" to PHYSICIAN REFERRAL manually." : null].filter(_temp17);
    const demographicsReviewReasons = [workflowNote?.includes("could not be matched in Athena and was not added to the patient's care team") ? "Add the unmatched referring provider to the Athena care team manually." : null, ensurePatientStatus === "completed" && patientContactSyncGaps.includes(PatientContactSyncGap.Phone) ? "Enter the patient's phone number in Athena manually \u2014 it did not sync." : null, ensurePatientStatus === "completed" && patientContactSyncGaps.includes(PatientContactSyncGap.Email) ? "Enter the patient's email in Athena manually \u2014 it did not sync." : null, ensurePatientStatus === "completed" && patientContactSyncGaps.includes(PatientContactSyncGap.HomeAddress) ? "Enter the patient's home address in Athena manually \u2014 it did not sync." : null].filter(_temp18);
    const pcpDisposition = referringProviderMarkedPcp ? "Yes \u2014 marked Is PCP at intake" : !isPrimaryCareProvider ? "No \u2014 not a primary care specialty" : primaryCareProviderAlreadyPresent ? "Already the patient's PCP in Athena" : "Yes \u2014 added as the patient's PCP";
    const careTeamStatus = taskStepStatus(taskSteps, [StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM]);
    const fullRegistrationStatus = ensurePatientStatus === "failed" ? "not_run" : ensurePatientStatus === "waiting" ? "waiting" : fullRegistrationReviewReasons.length > 0 ? "failed" : privacyNoticeOnFile === null ? "verification_required" : "completed";
    const hasEmergencyContactToEnter = opsCrossCheck.emergencyContactName !== NOT_AVAILABLE || opsCrossCheck.emergencyContactPhoneNumber !== NOT_AVAILABLE;
    const demographicsStatus = ensurePatientStatus === "failed" ? "not_run" : careTeamStatus !== "completed" ? careTeamStatus : demographicsReviewReasons.length > 0 ? "failed" : "completed";
    const dispositionStatus = taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION]);
    const unevaluatedInsuranceDecision = followThroughEligible ? "Pending" : "Not evaluated";
    const inNetworkDisplay = !insuranceRequirements ? unevaluatedInsuranceDecision : insuranceRequirements.clinicNetwork === "out_of_network" || insuranceRequirements.providerNetwork === "out_of_network" ? "No" : insuranceRequirements.clinicNetwork === "in_network" && insuranceRequirements.providerNetwork === "in_network" ? insuranceRequirements.networkVerification ? "Yes \u2014 confirmed by Ops" : "Yes \u2014 confirmed by insurance grid" : insuranceRequirements.clinicNetwork === "not_applicable" && insuranceRequirements.providerNetwork === "not_applicable" ? "Not applicable" : "Manual review";
    const referralRequirementDisplay = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement) : unevaluatedInsuranceDecision;
    const authorizationRequirementDisplay = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement) : unevaluatedInsuranceDecision;
    const insuranceCrossCheckItems = [{
      label: "Valerie plan",
      value: opsCrossCheck.primaryPlanLabel
    }, {
      label: "Valerie subscriber ID",
      value: opsCrossCheck.primarySubscriberId
    }, {
      label: "In network",
      value: inNetworkDisplay
    }, {
      label: "Referral",
      value: referralRequirementDisplay
    }, {
      label: "Authorization",
      value: authorizationRequirementDisplay
    }];
    const insuranceIntakeDetails = coverageApproval?.metadata ? <div className="space-y-3 rounded-md border p-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "Expected",
        value: coverageApproval.metadata.expectedCoverageCount
      }, {
        label: "In Athena",
        value: coverageApproval.metadata.currentCoverages.length
      }]} /><AthenaCoverageEvidence metadata={coverageApproval.metadata} /></div> : coverageReadback ? <div className="space-y-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "In Athena",
        value: coverageReadback.coverages.length
      }, {
        label: "Self pay",
        value: coverageReadback.primary.selfPay ? "Yes" : "No"
      }]} /><div className="space-y-1"><p className="font-medium">Currently in Athena</p>{coverageReadback.coverages.length === 0 ? <p>No coverage found.</p> : coverageReadback.coverages.map(_temp19)}</div></div> : followThroughEligible || assignmentStatus === "completed" ? <DecisionMetadata items={insuranceCrossCheckItems} /> : null;
    const steps = [{
      number: 1,
      stepKey: TPR_TASK_STEP_KEY.LOCATE_DOCUMENT,
      title: "Patient Chart",
      status: patientChartStatus,
      summary: "Copy and search by the document ID in Referral.",
      preview: [{
        label: "Patient",
        value: opsCrossCheck.patientName
      }, {
        label: "EHR ID",
        value: patientEhrId
      }],
      workflowStepKeys: [StepKey.ENSURE_PATIENT],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Patient",
          value: <CopyableContent text={`${opsCrossCheck.patientName} (DOB: ${opsCrossCheck.patientDateOfBirth})`} textClassName="text-xs" />
        }, {
          label: "EHR ID",
          value: <CopyableContent text={patientEhrId} textClassName="text-xs" />
        }, {
          label: "Document ID",
          value: <CopyableContent text={documentId} textClassName="text-xs" />
        }, {
          label: "Home phone",
          value: <CopyableContent text={opsCrossCheck.patientHomePhoneNumber} textClassName="text-xs" />
        }, {
          label: "Mobile phone",
          value: <CopyableContent text={opsCrossCheck.patientMobilePhoneNumber} textClassName="text-xs" />
        }, {
          label: "Work phone",
          value: <CopyableContent text={opsCrossCheck.patientWorkPhoneNumber} textClassName="text-xs" />
        }, {
          label: "Email",
          value: <CopyableContent text={opsCrossCheck.patientEmail} textClassName="text-xs" />
        }, {
          label: "Fax",
          value: <CopyableContent text={opsCrossCheck.patientFaxNumber} textClassName="text-xs" />
        }, {
          label: "Ethnicity",
          value: opsCrossCheck.patientEthnicity
        }, {
          label: "Race",
          value: opsCrossCheck.patientRace
        }, {
          label: "Language",
          value: opsCrossCheck.patientLanguage
        }]} />{patientDisplayId ? <Link href={`/patients/${patientDisplayId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">Open patient details<SquareArrowOutUpRight className="size-3.5" /></Link> : null}</div>
    }, {
      number: 2,
      stepKey: TPR_TASK_STEP_KEY.PROVIDER_LOCATION,
      title: "Receiving Provider and Location",
      status: assignmentConfirmationStatus,
      workflowStepKeys: [StepKey.RESOLVE_RECEIVING_ASSIGNMENT, StepKey.TPR_CAMELBACK_FOLLOW_THROUGH],
      preview: [{
        label: "Patient type",
        value: patientLabel
      }, {
        label: "Provider",
        value: resolvedReceiving.providerLabel
      }, {
        label: "Location",
        value: resolvedReceiving.locationLabel
      }],
      summary: usesRetiredUsualProviderRule ? "This legacy assignment used Athena's usual provider and must be checked against qualifying appointment history." : assignmentNeedsReview ? "Provider and location require manual review before downstream referral work." : "Verify we selected the correct receiving provider and location.",
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Patient type",
          value: patientLabel
        }, {
          label: "Provider",
          value: <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "NPI",
          value: <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "Location",
          value: <CopyableContent text={resolvedReceiving.locationLabel || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "Resolution",
          value: usesRetiredUsualProviderRule ? "Verify appointment history" : assignmentRequiresManualReview ? "Manual review" : "Confirmed"
        }]} />{providerAssignmentEditable ? <TprReceivingAssignmentEditor task={task} currentProviderNpi={resolvedReceiving.providerNpi || null} currentDepartmentId={resolvedReceiving.locationEhrId || null} /> : null}<ProviderSelectionChecklist evidence={providerSelectionEvidence} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} referralProviderName={opsCrossCheck.receivingProviderName} referralProviderNpi={opsCrossCheck.receivingProviderNpi} diagnosis={opsCrossCheck.diagnosis} diagnosisCategories={opsCrossCheck.diagnosisCategories} selectedProviderName={resolvedReceiving.providerLabel || NOT_AVAILABLE} selectedLocation={resolvedReceiving.locationLabel || NOT_AVAILABLE} />{resolvedReceiving.advisories.length > 0 ? <div className="space-y-1 rounded-md border border-warn/30 bg-warn/5 p-3"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.advisories.map(_temp20)}</ul></div> : null}{assignmentNeedsReview && resolvedReceiving.flags.length > 0 ? <><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.flags.map(_temp21)}</ul>{unresolvedUpcomingProviders.length > 0 ? <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/30 bg-warn/5 p-3"><span className="text-xs font-medium text-muted-foreground">Upcoming appointment provider</span>{unresolvedUpcomingProviders.map(_temp22)}</div> : null}</> : null}</div>,
      continuation: receivingAssignmentApproval ? {
        instruction: "After confirming the provider and location in Athena, continue the workflow.",
        control: <TprReceivingAssignmentContinueButton task={task} />
      } : undefined
    }, {
      number: 3,
      stepKey: TPR_TASK_STEP_KEY.DEMOGRAPHICS_CARE_TEAM,
      title: "Demographics and Care Team",
      preview: [{
        label: "PCP",
        value: pcpDisposition
      }],
      status: demographicsStatus,
      phase: hasEmergencyContactToEnter ? TaskStepPhase.ACTION_NEEDED : undefined,
      detail: hasEmergencyContactToEnter ? "Automation can't add the emergency contact. Enter it in Athena, then mark this updated." : undefined,
      workflowStepKeys: [StepKey.ENSURE_PATIENT, StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM],
      summary: hasEmergencyContactToEnter ? "Add the emergency contact to Athena by hand, then review patient and care team details in Quickview." : "Review patient details, care team details, and emergency contact separately in Quickview.",
      content: <div className="space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Patient Details</p><DecisionMetadata items={[{
            label: "Patient",
            value: opsCrossCheck.patientName
          }, {
            label: "DOB",
            value: opsCrossCheck.patientDateOfBirth
          }, {
            label: "Home phone",
            value: opsCrossCheck.patientHomePhoneNumber
          }, {
            label: "Mobile phone",
            value: opsCrossCheck.patientMobilePhoneNumber
          }, {
            label: "Work phone",
            value: opsCrossCheck.patientWorkPhoneNumber
          }, {
            label: "Email",
            value: opsCrossCheck.patientEmail
          }, {
            label: "Home address",
            value: opsCrossCheck.patientHomeAddress
          }]} /></div><div className="space-y-3 rounded-md border p-3"><p className="text-sm font-medium">Care Team</p><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Usual Provider</p><DecisionMetadata items={resolvedReceiving.usualProviderWriteSafe ? [{
              label: "Provider",
              value: <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
            }, {
              label: "NPI",
              value: <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
            }, {
              label: "Result",
              value: "Updated for new patient"
            }] : [{
              label: "Result",
              value: "Left unchanged for established patient"
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referring Provider</p><DecisionMetadata items={[{
              label: "Name",
              value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
            }, {
              label: "NPI",
              value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
            }, {
              label: "Address",
              value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
            }, {
              label: "Specialty",
              value: opsCrossCheck.referringProviderSpecialty
            }, {
              label: "Added as PCP",
              value: pcpDisposition
            }]} /></div></div><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Emergency Contact</p><DecisionMetadata items={[{
            label: "Name",
            value: <CopyableContent text={opsCrossCheck.emergencyContactName} textClassName="text-xs" />
          }, {
            label: "Relationship",
            value: <CopyableContent text={opsCrossCheck.emergencyContactRelationship} textClassName="text-xs" />
          }, {
            label: "Phone",
            value: <CopyableContent text={opsCrossCheck.emergencyContactPhoneNumber} textClassName="text-xs" />
          }]} /></div>{demographicsReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{demographicsReviewReasons.map(_temp23)}</ul> : null}</div>
    }, {
      number: 4,
      stepKey: TPR_TASK_STEP_KEY.INSURANCE,
      title: "Insurance",
      preview: [{
        label: "Plan",
        value: opsCrossCheck.primaryPlanLabel
      }, {
        label: "In network",
        value: insuranceRequirements ? inNetworkDisplay : null
      }],
      status: eligibilityTimedOut ? "review_needed" : insuranceStatus,
      phase: coverageApproval || eligibilityTimedOut ? TaskStepPhase.ACTION_NEEDED : undefined,
      workflowStepKeys: [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE, StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY, StepKey.SYNC_ATHENA_COVERAGES, StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS],
      summary: coverageApproval ? coverageApproval.metadata?.instruction ?? "Confirm the Athena coverage before continuing." : coverageReadback?.primary.selfPay ? "Self Pay \u2014 no network or eligibility checks apply." : insuranceRequirements ? `In network: ${inNetworkDisplay}. Athena eligibility: ${insuranceStatusDisplay}.` : coverageReadbackComplete ? "Coverage confirmed. Waiting on Athena eligibility and network/requirements decisions." : assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : "Waiting for the coverage write, Athena eligibility, and network/requirements decisions.",
      content: <div className="space-y-3">{athenaCoverageDrifted ? <p className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-foreground">Athena's primary insurance has changed since this referral synced. The network and eligibility results below were decided against the coverage we wrote, not the one on the chart now — re-check them before relying on them.</p> : null}<div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">{opsCrossCheck.primaryCoverageSlot.title}</p><DecisionMetadata items={[{
            label: "Plan (from Valerie)",
            value: <span className="inline-flex items-center gap-1.5"><CopyableContent text={opsCrossCheck.primaryPlanLabel} textClassName="text-xs" />{opsCrossCheck.isSpecialCaseInsurance ? <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn">Special Case Insurance</Badge> : null}</span>
          }, {
            label: "Subscriber ID (from Valerie)",
            value: <CopyableContent text={opsCrossCheck.primarySubscriberId} textClassName="text-xs" />
          }, {
            label: "Policy holder",
            value: <CopyableContent text={opsCrossCheck.primaryPolicyHolder} textClassName="text-xs" />
          }, {
            label: "Plan (in Athena, live)",
            value: <CopyableContent text={livePrimaryAthenaPlan} textClassName="text-xs" />
          }, {
            label: "Synced to Athena",
            value: primaryCoverageInAthena ? "Yes" : "No"
          }, {
            label: "In network",
            value: coverageReadback?.primary.selfPay ? "N/A \u2014 Self Pay" : inNetworkDisplay
          }]} />{coverageReadback && insuranceRequirements ? <TprNetworkStatus task={task} steps={taskSteps} coverage={coverageReadback} requirements={insuranceRequirements} /> : null}<DecisionMetadata items={[{
            label: "Coverage eligibility",
            value: followThroughEligible ? insuranceStatusDisplay : skippedFollowThroughSummary
          }]} />{followThroughEligible && insuranceStatusDisplay === "Unverified" && !coverageReadback?.primary.selfPay ? <p className="text-sm">Coverage eligibility still needs verification.</p> : null}</div><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">{opsCrossCheck.secondaryCoverageSlot.title}</p>{opsCrossCheck.secondaryPlanLabel || coverageReadback?.secondary || athenaCoverageLive.secondary ? <DecisionMetadata items={[{
            label: "Plan (from Valerie)",
            value: <span className="inline-flex items-center gap-1.5"><CopyableContent text={opsCrossCheck.secondaryPlanLabel || NOT_AVAILABLE} textClassName="text-xs" />{opsCrossCheck.isSecondarySpecialCaseInsurance ? <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn">Special Case Insurance</Badge> : null}</span>
          }, {
            label: "Subscriber ID (from Valerie)",
            value: <CopyableContent text={opsCrossCheck.secondarySubscriberId || NOT_AVAILABLE} textClassName="text-xs" />
          }, {
            label: "Policy holder",
            value: <CopyableContent text={opsCrossCheck.secondaryPolicyHolder || NOT_AVAILABLE} textClassName="text-xs" />
          }, {
            label: "Plan (in Athena, live)",
            value: <CopyableContent text={liveSecondaryAthenaPlan} textClassName="text-xs" />
          }, {
            label: "Synced to Athena",
            value: athenaCoverageLive.secondary || coverageReadback?.secondary ? "Yes" : "No"
          }]} /> : <p className="text-sm text-muted-foreground">No secondary insurance was captured from the referral or found in Athena.</p>}</div><details className="text-xs"><summary className="cursor-pointer text-muted-foreground">More insurance detail</summary><div className="mt-3 space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Coverage</p><DecisionMetadata items={[{
                label: "Self pay",
                value: coverageReadback?.primary.selfPay ? "Yes" : "No"
              }]} /><TprStediEligibilityValues referralId={referralId} />{insuranceIntakeDetails ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">Show Athena coverage detail</summary><div className="mt-2">{insuranceIntakeDetails}</div></details> : null}</div>{insuranceRequirements?.reviewReasons.length ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Network Review Reasons</p><ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">{insuranceRequirements.reviewReasons.map(_temp24)}</ul></div> : null}</div></details></div>,
      continuation: coverageApproval ? {
        instruction: "Choose each policy from Valerie or Athena, including its subscriber details, then sync to continue.",
        control: <AthenaCoverageResolutionDialog taskId={task.id} />
      } : eligibilityTimedOut ? {
        instruction: "Athena did not return a complete eligibility response in time. Review coverage in Athena, then recheck eligibility.",
        control: <TprManualStepContinueButton task={task} stepKey={StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY} />
      } : undefined
    }, {
      number: 5,
      stepKey: TPR_TASK_STEP_KEY.REFERRAL_AUTHORIZATION,
      title: "Referral Authorization",
      status: resolveConsolidatedStatus([pcpFaxStatus, authorizationStatus]),
      phase: referralAuthNeedsOutboundFax ? TaskStepPhase.ACTION_NEEDED : undefined,
      detail: referralAuthNeedsOutboundFax ? "This authorization needs an outbound fax, which isn't automated. Send it from Athena, then continue." : undefined,
      workflowStepKeys: [StepKey.TPR_PCP_REFERRAL_FAX, StepKey.TPR_REFERRAL_AUTHORIZATION],
      summary: opsKind === "pcp_referral_fax" ? opsApproval?.metadata?.instruction ?? "This plan requires the referral to come from the PCP. Export the chart and send the outbound fax." : opsKind === "authorization_review" ? opsApproval?.metadata?.instruction ?? "Review the Athena referral authorization object, then continue." : opsKind === "manual_authorization" ? opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION : referralAuthorization?.outcome === "created" ? `${referralAuthorization.write === "authorization" ? "Authorization" : "Paper referral"} object ${referralAuthorization.alreadyExisted ? "already existed" : "was created"} in Athena${referralAuthorization.pending ? " with number pending" : ""}.` : referralAuthorization?.outcome === "needs_pending_request" ? "No authorization number is on file. An outbound request letter is required next." : tprReferralAuthNeedsReview(referralAuthorization, insuranceRequirements) ? "No Athena object was created because the authorization requirements need manual review." : referralAuthorization?.outcome === "not_needed" ? `No Athena object was needed (${formatFromUnderscoreToHumanReadable(referralAuthorization.reason)}).` : followThroughEligible ? "Waiting for network, authorization, and referral-requirement decisions." : skippedFollowThroughSummary,
      content: <div className="space-y-3">{insuranceRequirements ? null : <p className="text-sm text-muted-foreground">{followThroughEligible ? "Waiting for network/requirements decision." : skippedFollowThroughSummary}</p>}{insuranceRequirements && !showPcpReferralRequirement && !showReferralRequirement ? <p className="text-sm text-muted-foreground">This plan does not require a referral or authorization.</p> : null}{showPcpReferralRequirement ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">PCP Referral Requirement</p><DecisionMetadata items={[{
            label: "PCP referral",
            value: "Required"
          }, {
            label: "Referring is PCP",
            value: isPrimaryCareProvider ? "Yes" : "No"
          }]} /></div> : null}{showReferralRequirement ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Referral Requirement</p><DecisionMetadata items={[{
            label: "Referral",
            value: formatFromUnderscoreToHumanReadable(insuranceRequirements?.referralRequirement ?? NOT_AVAILABLE)
          }]} />{showAuthorizationRequirement ? null : authorizationDetails}</div> : null}{showAuthorizationRequirement ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Authorization Requirement</p><DecisionMetadata items={[{
            label: "Authorization",
            value: formatFromUnderscoreToHumanReadable(insuranceRequirements?.authorizationRequirement ?? NOT_AVAILABLE)
          }]} />{authorizationDetails}</div> : null}{insuranceRequirements ? null : <TprSyncAuthButtons canUploadLetter={referralAuthNeedsPendingUpload} enabled={Boolean(task.referral && patientEhrId !== NOT_AVAILABLE)} taskId={task.id} />}{pcpFaxStatus === "review_needed" || authorizationStatus === "review_needed" ? <details className="text-xs"><summary className="text-muted-foreground">How to complete this manually</summary><div className="mt-2 space-y-3"><div><p className="font-medium text-foreground">Referral Required</p><ul className="list-disc space-y-1 pl-5 text-muted-foreground"><li>If a referral is required, confirm the received referral automatically created an object under the Authorization section.</li><li>If it did not, manually create the referral object with:<ul className="list-disc space-y-1 pl-5"><li>Insurance / authorization number: always "paper"</li><li>Referral dates — default (if not provided): today through 1 year later</li><li>Number of visits — default (if not provided): 12 visits</li><li>ICD-10 codes</li><li>Referring provider</li><li>Specialty</li><li>Only if listed on the referral doc: receiving provider and department</li></ul></li></ul></div><div><p className="font-medium text-foreground">Referral Authorization Required</p><ul className="list-disc space-y-1 pl-5 text-muted-foreground"><li>Same workflow as above, but enter the authorization number in the Insurance / authorization number field.</li><li>Only send a fax if there is no authorization number. When sending an authorization request for a missing auth number:<ul className="list-disc space-y-1 pl-5"><li>Manually export the doc to the referring provider via AthenaFax.</li><li>Add an authorization object with "Pending" in the auth number field.</li><li>In all notes (patient notes, document internal note, action note): "Referral Authorization Requested [Date]"</li></ul></li></ul></div></div></details> : null}</div>,
      continuation: opsKind === "pcp_referral_fax" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After sending the outbound PCP referral fax from Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="PCP referral fax confirmed" successDescription="The workflow is continuing with authorization." />
      } : opsKind === "authorization_review" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After reviewing the Athena referral authorization object, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Authorization reviewed" successDescription="The workflow is continuing with the remaining referral steps." />
      } : opsKind === "manual_authorization" ? {
        instruction: opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION,
        control: <TprOpsContinueButton task={task} successTitle="Manual authorization recorded" successDescription="The workflow is continuing with the remaining referral steps." />
      } : undefined
    }, {
      number: 6,
      stepKey: TPR_TASK_STEP_KEY.OUTBOUND_FAXES,
      detail: "Sending these faxes is still partly manual. Send them from Athena, then continue.",
      title: "Outbound Faxes",
      status: outboundFaxStatus,
      workflowStepKeys: [StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "auth_request_upload" ? "Export the patient chart in Athena manually, then upload the request letter. The letter is not uploaded automatically." : outboundFaxStatus === "not_needed" ? "No outbound fax is required for this referral." : "Send the required outbound fax(es) from Athena, then upload where applicable.",
      content: <div className="space-y-3">{referralAuthNeedsPendingUpload || authRequestUploaded ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Authorization Request Letter</p><DecisionMetadata items={[{
            label: "Request letter",
            value: authRequestUploaded ? "Uploaded" : "Waiting for ops"
          }]} />{outboundFaxStatus === "review_needed" ? <ol className="list-decimal space-y-1 pl-5 text-sm"><li>Export the patient chart in Athena. Do not skip this export.</li><li>Click Continue below to upload the request letter. The letter is not uploaded automatically.</li><li>Athena then receives a Referral Auth object with number pending.</li></ol> : null}</div> : null}{hasMissingInformation ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Missing Patient Phone Packet</p><DecisionMetadata items={[{
            label: "Missing-info packet",
            value: missingInfoUploadComplete ? "Sent to Athena chart" : "Waiting for automation"
          }]} /><p className="text-sm text-muted-foreground">Generated automatically when the patient phone number is missing — no upload needed here. Once it appears in the Athena chart, send it from Athena like any other outbound fax.</p></div> : null}{outboundFaxStatus === "not_needed" ? <p className="text-sm text-muted-foreground">Nothing to send — the referral authorization doesn't need an outbound request, and the patient phone number isn't missing.</p> : null}</div>,
      continuation: opsKind === "auth_request_upload" ? {
        instruction: "After exporting the chart in Athena, click Continue to upload the request letter.",
        control: <TprOpsContinueButton task={task} successTitle="Request letter queued" successDescription="The workflow will write the pending authorization and upload the letter." />
      } : undefined
    }, {
      number: 7,
      stepKey: TPR_TASK_STEP_KEY.FULL_REGISTRATION,
      title: "Full Registration",
      status: fullRegistrationStatus,
      summary: "Open Full Registration and verify communication consent, privacy, and referral source.",
      workflowStepKeys: [StepKey.ENSURE_PATIENT],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Calls",
          value: "Set to Yes only if blank \u2014 existing value is not overridden"
        }, {
          label: "Texts",
          value: "Set to Yes only if blank \u2014 existing value is not overridden"
        }, {
          label: "Privacy Notice",
          value: privacyNoticeOnFile === null ? "Not read back" : privacyNoticeOnFile ? "Selected" : "Not selected"
        }, {
          label: "How did you hear about us",
          value: "Set to Physician Referral only if blank \u2014 existing value is not overridden"
        }]} />{fullRegistrationReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-warn-foreground">{fullRegistrationReviewReasons.map(_temp25)}</ul> : null}</div>
    }, {
      number: 8,
      stepKey: TPR_TASK_STEP_KEY.PROCESS_REFERRAL_DOCUMENT,
      detail: "Parts of the referral document have to be keyed into Athena by hand. Enter them, then mark this updated.",
      title: "Referral Document",
      status: taskStepStatus(taskSteps, [StepKey.REFERRAL_DOCUMENT_METADATA]),
      summary: "Referral metadata is written to Athena in the order ops should verify it.",
      workflowStepKeys: [StepKey.REFERRAL_DOCUMENT_METADATA],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Document ID",
          value: <CopyableContent text={documentId} textClassName="text-xs" />
        }]} /><div className="space-y-3 rounded-md border p-3"><p className="text-sm font-medium">Referral Fields</p><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1. Referring Provider</p><DecisionMetadata items={[{
              label: "Name",
              value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
            }, {
              label: "NPI",
              value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
            }, {
              label: "Address",
              value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">2. New or Established Patient</p><DecisionMetadata items={[{
              label: "Patient type",
              value: <CopyableContent text={patientLabel} textClassName="text-xs" />
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">3. Date Received</p><DecisionMetadata items={[{
              label: "Date received",
              value: <CopyableContent text={opsCrossCheck.dateReceived} textClassName="text-xs" />
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">4. Urgency</p><DecisionMetadata items={[{
              label: "Urgent",
              value: urgency.isUrgent ? "Yes" : "No"
            }, ...(urgency.reason ? [{
              label: "Reason",
              value: urgency.reason
            }] : []), {
              label: "Scheduling priority",
              value: urgency.schedulingPriority || NOT_AVAILABLE
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">5. Internal Note</p><DecisionMetadata items={[{
              label: "Internal Note",
              value: <CopyableContent text={internalNoteToEnter} textClassName="text-xs" />
            }]} /></div></div></div>
    }, {
      number: 9,
      stepKey: TPR_TASK_STEP_KEY.DOCUMENT_UPLOAD,
      title: "Document Upload",
      status: taskStepStatus(taskSteps, [StepKey.SUPPORTING_SECTIONS_UPLOAD]),
      summary: !documentUploads.length ? "No documents were selected for upload." : resultDocumentsToLabel.length ? "Confirm the expected documents were uploaded to Athena, then add the document label to each lab and imaging result so it can be closed." : "Confirm the expected documents were uploaded to Athena.",
      workflowStepKeys: [StepKey.SUPPORTING_SECTIONS_UPLOAD],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Documents",
          value: documentUploads.length
        }, ...(resultDocumentsToLabel.length ? [{
          label: "Need document label",
          value: resultDocumentsToLabel.length
        }] : [])]} /><div className="space-y-1"><p className="text-sm font-medium">Expected Athena uploads</p>{documentUploads.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{documentUploads.map(_temp26)}</ul> : <p className="text-sm text-muted-foreground">No documents were selected for upload.</p>}</div></div>
    }, {
      number: 10,
      stepKey: TPR_TASK_STEP_KEY.QUICKVIEW,
      title: "Quickview Note",
      status: notesComplete ? "verification_required" : dispositionStatus,
      workflowStepKeys: [],
      summary: notesComplete ? "Confirm the note above appears correctly on the patient's Quickview tab in Athena." : "Waiting for the final note to be written before this can be verified.",
      content: notesComplete ? <p className="text-sm">Confirm the final note appears correctly on the patient's Quickview tab in Athena.</p> : undefined
    }, {
      number: 11,
      stepKey: TPR_TASK_STEP_KEY.BUCKET_ASSIGNMENT,
      detail: "Choosing the bucket is ops' call. Assign it in Athena, then mark this updated.",
      title: "Bucket Assignment",
      status: resolveConsolidatedStatus([closeDocumentStatus, dispositionStatus === "failed" ? "not_run" : !workflowStillProcessing && notesComplete && (!hasMissingInformation || missingInfoUploadComplete) ? "review_needed" : taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX])]),
      workflowStepKeys: [StepKey.TPR_CLOSE_DOCUMENT, StepKey.REFERRAL_DISPOSITION, StepKey.SCHEDULING_RECOMMENDATION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "close_document" ? opsApproval?.metadata?.instruction ?? "There is an upcoming appointment within 90 days. Close the document in Athena, then continue." : hasMissingInformation && !missingInfoUploadComplete ? "Waiting for the missing-information document to be prepared." : referralAuthNeedsPendingUpload && authRequestUploaded ? "Confirm the authorization-request letter on the chart, route, and complete." : hasMissingInformation ? "Send the missing-information document, route the referral, and complete only after all manual actions are done." : "Confirm the recommended destination bucket \u2014 SS Cardio, Biltmore, or Close \u2014 and complete the task.",
      content: <div className="space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Bucket</p><DecisionMetadata items={[{
            label: "Suggested bucket",
            value: <CopyableContent text={bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? NO_CONFIDENT_BUCKET_SUGGESTION : SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} />
          }, {
            label: "Missing information",
            value: hasMissingInformation ? "Required" : "No"
          }, {
            label: "Authorization request",
            value: authRequestUploaded ? "Uploaded" : "Not required"
          }]} />{decisionTrace.length > 0 ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">How this was determined</summary><ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">{decisionTrace.map(_temp27)}</ul></details> : null}</div>{isReferralAuth ? <TprChartReferralAuthDocuments documents={chartReferralAuthDocuments} /> : null}{!resolvedReceiving.routedToSscardio && !workflowStillProcessing && notesComplete && task.type === TaskType.REFERRAL && task.referral && <TaskSchedulingRecommendation taskId={task.id} referral={task.referral} preferAgenticReceivingProvider={true} />}<div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Action Notes</p><DecisionMetadata items={[{
            label: "Action Notes",
            value: <CopyableContent text={actionNote} textClassName="text-xs" />
          }]} /></div></div>,
      continuation: opsKind === "close_document" ? {
        instruction: "After closing the document in Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Document close confirmed" successDescription="The workflow is continuing with remaining follow-through." />
      } : undefined
    }];
    const failedTaskStepFor = card => taskSteps.filter(row => row.status === TaskStepStatus.FAILED && card.workflowStepKeys.includes(row.stepKey as StepKey)).toSorted(_temp28)[0];
    const failureReasonOf = _temp29;
    const stoppedOnFailureCard = steps.find(card_0 => {
      const row_1 = failedTaskStepFor(card_0);
      return row_1 !== undefined && !isTprContinuedFailure(task.status, taskSteps, row_1);
    });
    let sequenceOpen = true;
    let sequenceBlockedBy = null;
    const phasedSteps = steps.map(step_6 => {
      const offersManualBypass = Boolean(manualBypassStepKey && step_6.workflowStepKeys.includes(manualBypassStepKey));
      const pinnedPhase = offersManualBypass ? TaskStepPhase.ACTION_NEEDED : step_6.phase ?? TPR_PINNED_STEP_PHASE[step_6.stepKey];
      const failedTaskStep = failedTaskStepFor(step_6);
      const isFailedSync = failedTaskStep !== undefined;
      const continuedPastFailure = failedTaskStep !== undefined && isTprContinuedFailure(task.status, taskSteps, failedTaskStep);
      const status_0 = isFailedSync ? "failed" : offersManualBypass ? "review_needed" : step_6.status;
      const naturalPhase = resolveTaskStepPhase(status_0, pinnedPhase);
      const verdict = verdictByStepKey.get(step_6.stepKey)?.verdict;
      const ownsPendingGate = step_6.continuation !== undefined || pendingApproval !== null && pendingApproval.stepKey !== null && step_6.workflowStepKeys.includes(pendingApproval.stepKey);
      const confirmed = !isFailedSync && !ownsPendingGate && !offersManualBypass && verdict === VerificationResult.CORRECT;
      const signOffOnContinue = continuedPastFailure && verdict !== VerificationResult.INCORRECT && step_6.workflowStepKeys.every(stepKey => taskSteps.some(row_2 => row_2.stepKey === stepKey && (row_2.status === TaskStepStatus.COMPLETED || row_2 === failedTaskStep)));
      const presented = sequenceOpen || isFailedSync || ownsPendingGate || offersManualBypass;
      const phase = presented ? naturalPhase : null;
      const awaitingStep = presented ? null : sequenceBlockedBy;
      if (sequenceOpen && !continuedPastFailure && step_6.status !== "not_needed" && !(naturalPhase !== null && confirmed)) {
        sequenceOpen = false;
        sequenceBlockedBy = step_6;
      }
      return {
        step: {
          ...step_6,
          status: status_0,
          phase: pinnedPhase
        },
        failedStepKey: failedTaskStep?.stepKey as StepKey | undefined ?? null,
        failureReason: failedTaskStep ? failureReasonOf(failedTaskStep) : null,
        continuedPastFailure,
        signOffOnContinue,
        phase,
        awaitingStep,
        naturalPhase
      };
    });
    const timelineNotice = waitingForAssociatedReferral ? "Waiting for the matching referral for this patient to finish syncing. This authorization continues once it does, or after two business days." : phasedSteps.some(_temp30) ? null : workflowStillProcessing ? "Automation is still running. This timeline will update as each durable workflow step completes." : pendingApproval ? "This referral is held at an approval no step can resolve. It needs engineering \u2014 escalate rather than closing it out." : "No manual actions remain.";
    let t5;
    if ($[99] !== isReferralAuth) {
      t5 = isReferralAuth ? <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground"><p className="font-medium">Referral Authorization</p><p>This document is a standalone referral authorization, not a referral.</p></div> : null;
      $[99] = isReferralAuth;
      $[100] = t5;
    } else {
      t5 = $[100];
    }
    let t6;
    if ($[101] !== timelineNotice) {
      t6 = timelineNotice ? <p className="text-sm text-muted-foreground">{timelineNotice}</p> : null;
      $[101] = timelineNotice;
      $[102] = t6;
    } else {
      t6 = $[102];
    }
    t4 = <div className="space-y-8 pb-8">{t5}{t6}{phasedSteps.map(t7 => {
        const {
          step: step_7,
          naturalPhase: naturalPhase_0,
          awaitingStep: awaitingStep_0,
          failedStepKey,
          failureReason,
          continuedPastFailure: continuedPastFailure_0,
          signOffOnContinue: signOffOnContinue_0
        } = t7;
        return <TimelineStepCard key={step_7.number} task={task} step={step_7} failedStepKey={failedStepKey} bypassStepKey={manualBypassStepKey && step_7.workflowStepKeys.includes(manualBypassStepKey) ? manualBypassStepKey : null} failureReason={failureReason} continuedPastFailure={continuedPastFailure_0} signOffOnContinue={signOffOnContinue_0} blockedBy={naturalPhase_0 === null && stoppedOnFailureCard && stoppedOnFailureCard.number !== step_7.number ? stoppedOnFailureCard : null} awaitingStep={awaitingStep_0} />;
      })}</div>;
    $[8] = actionNote;
    $[9] = athenaCoverageLive.primary;
    $[10] = athenaCoverageLive.secondary;
    $[11] = bucketLabel;
    $[12] = chartReferralAuthDocuments;
    $[13] = chartReferralAuths;
    $[14] = chartRequestLetters;
    $[15] = coverageReadback;
    $[16] = decisionTrace;
    $[17] = documentId;
    $[18] = documentUploads;
    $[19] = followThroughEligible;
    $[20] = hasMissingInformation;
    $[21] = insuranceRequirements;
    $[22] = insuranceStatusDisplay;
    $[23] = internalNoteToEnter;
    $[24] = isPrimaryCareProvider;
    $[25] = isProviderFirst;
    $[26] = isReferralAuth;
    $[27] = opsCrossCheck.authorizationEndDate;
    $[28] = opsCrossCheck.authorizationNumber;
    $[29] = opsCrossCheck.authorizationServices;
    $[30] = opsCrossCheck.authorizationStartDate;
    $[31] = opsCrossCheck.authorizationStatus;
    $[32] = opsCrossCheck.authorizationVisits;
    $[33] = opsCrossCheck.dateReceived;
    $[34] = opsCrossCheck.diagnosis;
    $[35] = opsCrossCheck.diagnosisCategories;
    $[36] = opsCrossCheck.emergencyContactName;
    $[37] = opsCrossCheck.emergencyContactPhoneNumber;
    $[38] = opsCrossCheck.emergencyContactRelationship;
    $[39] = opsCrossCheck.isSecondarySpecialCaseInsurance;
    $[40] = opsCrossCheck.isSpecialCaseInsurance;
    $[41] = opsCrossCheck.patientDateOfBirth;
    $[42] = opsCrossCheck.patientEmail;
    $[43] = opsCrossCheck.patientEthnicity;
    $[44] = opsCrossCheck.patientFaxNumber;
    $[45] = opsCrossCheck.patientHomeAddress;
    $[46] = opsCrossCheck.patientHomePhoneNumber;
    $[47] = opsCrossCheck.patientLanguage;
    $[48] = opsCrossCheck.patientMobilePhoneNumber;
    $[49] = opsCrossCheck.patientName;
    $[50] = opsCrossCheck.patientRace;
    $[51] = opsCrossCheck.patientWorkPhoneNumber;
    $[52] = opsCrossCheck.primaryCoverageSlot.title;
    $[53] = opsCrossCheck.primaryPlanLabel;
    $[54] = opsCrossCheck.primaryPolicyHolder;
    $[55] = opsCrossCheck.primarySubscriberId;
    $[56] = opsCrossCheck.receivingProviderName;
    $[57] = opsCrossCheck.receivingProviderNpi;
    $[58] = opsCrossCheck.referringLocationAddress;
    $[59] = opsCrossCheck.referringProviderName;
    $[60] = opsCrossCheck.referringProviderNpi;
    $[61] = opsCrossCheck.referringProviderSpecialty;
    $[62] = opsCrossCheck.secondaryCoverageSlot.title;
    $[63] = opsCrossCheck.secondaryPlanLabel;
    $[64] = opsCrossCheck.secondaryPolicyHolder;
    $[65] = opsCrossCheck.secondarySubscriberId;
    $[66] = patientContactSyncGaps;
    $[67] = patientDisplayId;
    $[68] = patientEhrId;
    $[69] = patientLabel;
    $[70] = pendingApproval;
    $[71] = primaryCareProviderAlreadyPresent;
    $[72] = privacyNoticeOnFile;
    $[73] = providerAssignmentMarkedIncorrect;
    $[74] = providerSelectionEvidence;
    $[75] = receivingAssignmentConfirmed;
    $[76] = referralAuthorization;
    $[77] = referralId;
    $[78] = referralSourceOnFile;
    $[79] = referringProviderMarkedPcp;
    $[80] = resolvedReceiving.advisories;
    $[81] = resolvedReceiving.flags;
    $[82] = resolvedReceiving.locationEhrId;
    $[83] = resolvedReceiving.locationLabel;
    $[84] = resolvedReceiving.providerLabel;
    $[85] = resolvedReceiving.providerNpi;
    $[86] = resolvedReceiving.providerWriteSafe;
    $[87] = resolvedReceiving.routedToSscardio;
    $[88] = resolvedReceiving.usualProviderWriteSafe;
    $[89] = task;
    $[90] = taskSteps;
    $[91] = unresolvedUpcomingProviders;
    $[92] = upcomingAppointmentProviderSummary;
    $[93] = urgency.isUrgent;
    $[94] = urgency.reason;
    $[95] = urgency.schedulingPriority;
    $[96] = verdictByStepKey;
    $[97] = workflowStillProcessing;
    $[98] = t4;
  } else {
    t4 = $[98];
  }
  return t4;
}
function _temp30(entry_0) {
  return entry_0.phase !== null;
}
function _temp29(row_0) {
  return row_0.output?.error || row_0.output?.failureReason || row_0.output?.ccErrors ? errorFromStepOutput(row_0.output, row_0.stepKey).message : null;
}
function _temp28(left, right) {
  return left.stepIndex - right.stepIndex;
}
function _temp27(entry, index) {
  return <li key={`${entry.label}-${index}`}>{entry.label} <strong>{entry.outcome}</strong></li>;
}
function _temp26(t0) {
  const {
    label,
    resultDocumentLabel
  } = t0;
  return <li key={label}><CopyableContent text={label} />{resultDocumentLabel ? <div className="text-muted-foreground">Add document label: <CopyableContent text={resultDocumentLabel} /></div> : null}</li>;
}
function _temp25(reason_4) {
  return <li key={reason_4}>{reason_4}</li>;
}
function _temp24(reason_3) {
  return <li key={reason_3}>{reason_3}</li>;
}
function _temp23(reason_2) {
  return <li key={reason_2}>{reason_2}</li>;
}
function _temp22(provider) {
  return <Badge key={provider.providerEhrId ?? provider.label} variant="outline">{provider.label}{provider.providerEhrId ? ` · Athena ID ${provider.providerEhrId}` : ""}</Badge>;
}
function _temp21(reason_1) {
  return <li key={reason_1}>{reason_1}</li>;
}
function _temp20(advisory) {
  return <li key={advisory}>{advisory}</li>;
}
function _temp19(coverage) {
  return <p key={coverage.insuranceId}><strong>{coverage.casePolicy ? "Case policy" : coverage.sequence === 1 ? "Primary" : coverage.sequence === 2 ? "Secondary" : `Sequence ${coverage.sequence ?? "N/A"}`}:</strong>{" "}{coverage.planName ?? "Unknown plan"} · Package <CopyableContent text={coverage.insurancePackageId} />{coverage.subscriberId ? <>{" "}· Subscriber <CopyableContent text={coverage.subscriberId} /></> : null}</p>;
}
function _temp18(reason_0) {
  return reason_0 !== null;
}
function _temp17(reason) {
  return reason !== null;
}
function _temp16(file) {
  return file.type === FileType.INBOUND;
}
function _temp15(upload) {
  return upload.resultDocumentLabel !== null;
}
function _temp14(letter) {
  return <li key={letter.documentEhrId}><CopyableContent text={letter.documentEhrId} textClassName="text-xs" />{letter.documentDate ? ` · ${letter.documentDate}` : ""}{letter.description ? ` · ${letter.description}` : ""}</li>;
}
function _temp13(authorization_0) {
  return <li key={authorization_0.referralAuthId}><CopyableContent text={authorization_0.referralAuthId} textClassName="text-xs" />{authorization_0.number ? ` · ${authorization_0.number}` : ""}{authorization_0.startDate || authorization_0.endDate ? ` · ${authorization_0.startDate ?? "\u2014"}–${authorization_0.endDate ?? "\u2014"}` : ""}{authorization_0.expired ? " \xB7 expired" : ""}</li>;
}
function _temp12(status) {
  return status === "required" || status === "manual_review";
}
function _temp11(statuses) {
  if (statuses.includes("failed")) {
    return "failed";
  }
  if (statuses.includes("review_needed")) {
    return "review_needed";
  }
  if (statuses.includes("verification_required")) {
    return "verification_required";
  }
  if (statuses.includes("waiting")) {
    return "waiting";
  }
  if (statuses.includes("completed")) {
    return "completed";
  }
  if (statuses.includes("not_run")) {
    return "not_run";
  }
  return "not_needed";
}
function _temp10(step_5) {
  return step_5.stepKey === StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX && step_5.status === TaskStepStatus.COMPLETED;
}
function _temp9(step_4) {
  return step_4.stepKey === StepKey.REFERRAL_DISPOSITION && step_4.status === TaskStepStatus.COMPLETED;
}
function _temp8(step_3) {
  return step_3.stepKey === StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD && step_3.status === TaskStepStatus.COMPLETED && step_3.output?.uploaded === true;
}
function _temp7(step_2) {
  return step_2.stepKey === StepKey.READ_ATHENA_COVERAGE && step_2.status === TaskStepStatus.COMPLETED;
}
function _temp6(step_1) {
  return step_1.stepKey === StepKey.TPR_WAIT_FOR_ASSOCIATED_REFERRAL && step_1.status === TaskStepStatus.PAUSED;
}
function _temp5(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT;
}
interface TprReferralInstructionsProps {
  view: "information" | "steps";
  referral: Referral;
  task: Task;
  athenaData: AthenaReferralCardsData | null;
  patientEhrId: string;
  /** "New Patient Referral" / "Established Patient Referral" / "N/A" — see getAthenaReferralDocumentLabel. */
  patientLabel: string;
  upcomingAppointments: Appointment[];
  /** Past booked appointments (lookback window matching the new/established determination) for the
   * "Patient Type" sanity-check list — display-only, not the source of the New/Established label itself. */
  pastAppointments: Appointment[];
  documentId: string;
  isPrimaryCareProvider: boolean;
  referringProviderMarkedPcp: boolean;
  /** Whether the referring provider is already on the Athena care team as PRIMARY_CARE_PROVIDER —
   * suppresses the redundant "PCP" line in Step 3's care team card. See resolveTprCareTeamPolicy. */
  primaryCareProviderAlreadyPresent: boolean;
  /** One of TprSyncBucket's four values: "closed" | "sscardio" | "biltmorecardreferral" | "biltmorecardiosched" */
  bucketLabel: string;
  actionNote: string;
  /** Athena document priority (Urgent vs Normal) and why — shown in Step 8 before the internal note. */
  urgency: TprSyncChecklistUrgency;
  /** The note the disposition step will write, always carrying the
   * "Reviewed By Valerie Health (date) - <provider> - ..." prefix. Step 8 shows this rather than the
   * Athena read-back, which is still empty at that point in the workflow. */
  expectedInternalNote: string;
  /** Each decision-tree check that ran, in order, with its outcome — see resolveTprSyncChecklist. */
  decisionTrace: TprSyncChecklistTraceEntry[];
  /** Non-stopping context notes from the decision tree (insurance-sync issues, urgency reason, the
   * detailed "ops must manually verify..." explanation behind a short bucket-assignment label, etc). */
  flags: string[];
  /** "Eligible" | "Ineligible" | "Unverified" — same Athena-then-Stedi result the decision tree reads. */
  eligibilityDisplay: StediEligibilityDisplayLabel;
  documentUploads: TprDocumentUpload[];
  privacyNoticeOnFile: boolean | null;
  /** Athena's current `referralsourceid`, or null when blank. */
  referralSourceOnFile: string | null | undefined;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  receivingAssignmentConfirmed: boolean;
  followThroughEligible: boolean;
  coverageReadback: AthenaCoverageReadbackOutput | null;
  insuranceRequirements: TprInsuranceRequirementsOutput | null;
  referralAuthorization: TprReferralAuthorizationOutput | null;
  unresolvedUpcomingProviders: Array<{
    label: string;
    providerEhrId: string | null;
  }>;
  upcomingAppointmentProviderSummary: string | null;
  /** Persisted provider-first assignment, or the legacy display fallback for older tasks. */
  resolvedReceiving: {
    providerLabel: string | null;
    providerNpi: string | null;
    providerWriteSafe: boolean;
    usualProviderWriteSafe: boolean;
    locationLabel: string | null;
    /** Athena department id behind `locationLabel` — preselects the ops location picker. */
    locationEhrId: string | null;
    trace: string[];
    flags: string[];
    /** Non-blocking reviewer notices (e.g. the Terrace cardiology/neurology check) — always shown,
     * unlike `flags`, which only renders once the assignment already needs manual review. */
    advisories: string[];
    routedToSscardio: boolean;
    providerSelectionEvidence?: ReceivingAssignmentOutput["providerSelectionEvidence"];
  };
  chartReferralAuths: TprChartReferralAuth[];
  chartRequestLetters: TprChartRequestLetter[];
  isReferralAuth: boolean;
  chartReferralAuthDocuments: TprChartDocument[] | null;
}
export function TprReferralInstructions(t0) {
  const $ = _c(490);
  const {
    view,
    referral,
    task,
    athenaData,
    patientEhrId,
    patientLabel,
    upcomingAppointments,
    pastAppointments,
    documentId,
    isPrimaryCareProvider,
    referringProviderMarkedPcp,
    primaryCareProviderAlreadyPresent,
    bucketLabel,
    actionNote,
    urgency,
    expectedInternalNote,
    decisionTrace,
    flags,
    eligibilityDisplay,
    documentUploads,
    privacyNoticeOnFile,
    referralSourceOnFile,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    resolvedReceiving,
    chartReferralAuths,
    chartRequestLetters,
    isReferralAuth,
    chartReferralAuthDocuments
  } = t0;
  let t1;
  if ($[0] !== referral.referringLocation?.address) {
    t1 = getFormattedAddress(referral.referringLocation?.address) || NOT_AVAILABLE;
    $[0] = referral.referringLocation?.address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referringLocationAddress = t1;
  const referringProviderSpecialty = referral.referringProvider?.specialty || NOT_AVAILABLE;
  let t2;
  if ($[2] !== referral.referringProvider) {
    t2 = referral.referringProvider ? getPractitionerNameWithCredentials(referral.referringProvider) : NOT_AVAILABLE;
    $[2] = referral.referringProvider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const referringProviderName = t2;
  let t3;
  if ($[4] !== referral.receivingProvider) {
    t3 = referral.receivingProvider ? getPractitionerNameWithCredentials(referral.receivingProvider) : NOT_AVAILABLE;
    $[4] = referral.receivingProvider;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const receivingProviderName = t3;
  const patient = referral.patient;
  const emergencyContactName = patient?.emergencyContactName || NOT_AVAILABLE;
  const emergencyContactPhoneNumber = patient?.emergencyContactPhoneNumber || NOT_AVAILABLE;
  const emergencyContactRelationship = patient?.emergencyContactRelationshipToPatient || NOT_AVAILABLE;
  let t4;
  if ($[6] !== referral.primaryCoverage) {
    t4 = getCoverageSummary(referral.primaryCoverage);
    $[6] = referral.primaryCoverage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const primaryCoverageSummary = t4;
  let t5;
  if ($[8] !== referral.secondaryCoverage) {
    t5 = getCoverageSummary(referral.secondaryCoverage);
    $[8] = referral.secondaryCoverage;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const secondaryCoverageSummary = t5;
  let t6;
  if ($[10] !== referral.primaryCoverage || $[11] !== referral.secondaryCoverage) {
    t6 = getTprCoverageSlots(referral.primaryCoverage, referral.secondaryCoverage);
    $[10] = referral.primaryCoverage;
    $[11] = referral.secondaryCoverage;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const coverageSlots = t6;
  let t7;
  if ($[13] !== referral.dateReceived) {
    t7 = formatDateMmDdYyyy(referral.dateReceived) || NOT_AVAILABLE;
    $[13] = referral.dateReceived;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const dateReceived = t7;
  const schedulingPriority = referral.schedulingPriority || NOT_AVAILABLE;
  const athenaInternalNote = athenaData?.document?.internalNote || NOT_AVAILABLE;
  const internalNoteToEnter = athenaInternalNote.toLowerCase().includes(REVIEWED_BY_VALERIE_HEALTH_MARKER) ? athenaInternalNote : expectedInternalNote;
  let T0;
  let dataSourceEntries;
  let hasMissingInformation;
  let missingFields;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t8;
  let t9;
  if ($[15] !== actionNote || $[16] !== athenaData?.coverages || $[17] !== athenaData?.patientDetails || $[18] !== bucketLabel || $[19] !== chartReferralAuthDocuments || $[20] !== chartReferralAuths || $[21] !== chartRequestLetters || $[22] !== coverageReadback || $[23] !== coverageSlots.primary || $[24] !== coverageSlots.secondary || $[25] !== dateReceived || $[26] !== decisionTrace || $[27] !== documentId || $[28] !== documentUploads || $[29] !== eligibilityDisplay || $[30] !== emergencyContactName || $[31] !== emergencyContactPhoneNumber || $[32] !== emergencyContactRelationship || $[33] !== followThroughEligible || $[34] !== insuranceRequirements || $[35] !== internalNoteToEnter || $[36] !== isPrimaryCareProvider || $[37] !== isReferralAuth || $[38] !== pastAppointments || $[39] !== patient || $[40] !== patientEhrId || $[41] !== patientLabel || $[42] !== pendingApproval || $[43] !== primaryCareProviderAlreadyPresent || $[44] !== primaryCoverageSummary || $[45] !== privacyNoticeOnFile || $[46] !== receivingAssignmentConfirmed || $[47] !== receivingProviderName || $[48] !== referral || $[49] !== referralAuthorization || $[50] !== referralSourceOnFile || $[51] !== referringLocationAddress || $[52] !== referringProviderMarkedPcp || $[53] !== referringProviderName || $[54] !== referringProviderSpecialty || $[55] !== resolvedReceiving || $[56] !== secondaryCoverageSummary || $[57] !== task || $[58] !== taskSteps || $[59] !== unresolvedUpcomingProviders || $[60] !== upcomingAppointmentProviderSummary || $[61] !== upcomingAppointments || $[62] !== urgency || $[63] !== view) {
    t26 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedPastAppointments = sortAppointmentsMostRecentFirst(pastAppointments);
      const syncFailed = task.status === TaskStatus.FAILED;
      const insuranceStatusDisplay = coverageReadback?.primary.selfPay ? "Self Pay" : referral.primaryCoverage ? eligibilityDisplay : "Missing Insurance";
      const chartPatient = athenaData?.patientDetails ?? null;
      let t27;
      if ($[87] !== patient) {
        t27 = patient && getPatientPhoneNumber(patient)?.trim() || null;
        $[87] = patient;
        $[88] = t27;
      } else {
        t27 = $[88];
      }
      const referralPhone = t27;
      let t28;
      if ($[89] !== chartPatient) {
        t28 = chartPatient && getPatientPhoneNumber(chartPatient)?.trim() || null;
        $[89] = chartPatient;
        $[90] = t28;
      } else {
        t28 = $[90];
      }
      const chartPhone = t28;
      let t29;
      if ($[91] !== chartPatient?.homePhoneNumber || $[92] !== patient?.homePhoneNumber) {
        t29 = patient?.homePhoneNumber?.trim() || chartPatient?.homePhoneNumber?.trim() || null;
        $[91] = chartPatient?.homePhoneNumber;
        $[92] = patient?.homePhoneNumber;
        $[93] = t29;
      } else {
        t29 = $[93];
      }
      const homePhone = t29;
      let t30;
      if ($[94] !== chartPatient?.mobilePhoneNumber || $[95] !== patient?.mobilePhoneNumber) {
        t30 = patient?.mobilePhoneNumber?.trim() || chartPatient?.mobilePhoneNumber?.trim() || null;
        $[94] = chartPatient?.mobilePhoneNumber;
        $[95] = patient?.mobilePhoneNumber;
        $[96] = t30;
      } else {
        t30 = $[96];
      }
      const mobilePhone = t30;
      let t31;
      if ($[97] !== chartPatient?.workPhoneNumber || $[98] !== patient?.workPhoneNumber) {
        t31 = patient?.workPhoneNumber?.trim() || chartPatient?.workPhoneNumber?.trim() || null;
        $[97] = chartPatient?.workPhoneNumber;
        $[98] = patient?.workPhoneNumber;
        $[99] = t31;
      } else {
        t31 = $[99];
      }
      const workPhone = t31;
      let t32;
      if ($[100] !== chartPatient?.personalEmail || $[101] !== patient?.personalEmail) {
        t32 = patient?.personalEmail?.trim().toLowerCase() || chartPatient?.personalEmail?.trim().toLowerCase() || null;
        $[100] = chartPatient?.personalEmail;
        $[101] = patient?.personalEmail;
        $[102] = t32;
      } else {
        t32 = $[102];
      }
      const email = t32;
      let patientContactSyncGaps;
      let phoneDataSourceEntry;
      let syncedAddressValue;
      let t33;
      let t34;
      let t35;
      if ($[103] !== athenaData?.coverages || $[104] !== chartPatient || $[105] !== chartPhone || $[106] !== patient || $[107] !== referral.primaryCoverage || $[108] !== referralPhone) {
        const referralCoverageValue = getCoverageSummary(referral.primaryCoverage);
        const chartPrimaryCoverage = [...(athenaData?.coverages ?? [])].sort(_temp31)[0];
        const chartCoverageValue = getCoverageSummary(chartPrimaryCoverage ?? null);
        const referralAddressValue = patient?.homeAddress ? getFormattedAddress(patient.homeAddress) : null;
        const chartAddressValue = chartPatient?.homeAddress ? getFormattedAddress(chartPatient.homeAddress) : null;
        syncedAddressValue = isCompleteAddress(patient?.homeAddress) ? referralAddressValue : chartAddressValue ?? referralAddressValue;
        let t36;
        if ($[115] !== chartPatient || $[116] !== patient) {
          t36 = patient && chartPatient ? findPatientContactSyncGaps(patient, chartPatient) : [];
          $[115] = chartPatient;
          $[116] = patient;
          $[117] = t36;
        } else {
          t36 = $[117];
        }
        patientContactSyncGaps = t36;
        let t37;
        if ($[118] !== chartPhone || $[119] !== referralPhone) {
          t37 = resolveDataSourceEntry("Phone Number", referralPhone, chartPhone);
          $[118] = chartPhone;
          $[119] = referralPhone;
          $[120] = t37;
        } else {
          t37 = $[120];
        }
        phoneDataSourceEntry = t37;
        t33 = phoneDataSourceEntry;
        t34 = resolveDataSourceEntry("Insurance", referralCoverageValue, chartCoverageValue);
        t35 = resolveDataSourceEntry("Home Address", referralAddressValue, chartAddressValue);
        $[103] = athenaData?.coverages;
        $[104] = chartPatient;
        $[105] = chartPhone;
        $[106] = patient;
        $[107] = referral.primaryCoverage;
        $[108] = referralPhone;
        $[109] = patientContactSyncGaps;
        $[110] = phoneDataSourceEntry;
        $[111] = syncedAddressValue;
        $[112] = t33;
        $[113] = t34;
        $[114] = t35;
      } else {
        patientContactSyncGaps = $[109];
        phoneDataSourceEntry = $[110];
        syncedAddressValue = $[111];
        t33 = $[112];
        t34 = $[113];
        t35 = $[114];
      }
      let t36;
      if ($[121] !== t33 || $[122] !== t34 || $[123] !== t35) {
        t36 = [t33, t34, t35];
        $[121] = t33;
        $[122] = t34;
        $[123] = t35;
        $[124] = t36;
      } else {
        t36 = $[124];
      }
      dataSourceEntries = t36;
      const isCamelbackReferral = !resolvedReceiving.routedToSscardio;
      let t37;
      if ($[125] !== isCamelbackReferral || $[126] !== phoneDataSourceEntry) {
        t37 = isCamelbackReferral && phoneDataSourceEntry.missing ? [phoneDataSourceEntry] : [];
        $[125] = isCamelbackReferral;
        $[126] = phoneDataSourceEntry;
        $[127] = t37;
      } else {
        t37 = $[127];
      }
      missingFields = t37;
      hasMissingInformation = missingFields.length > 0;
      let t38;
      if ($[128] !== patient) {
        t38 = getPatientDisplayName(patient);
        $[128] = patient;
        $[129] = t38;
      } else {
        t38 = $[129];
      }
      const patientDisplayName = t38;
      const receivingLocationName = referral.receivingLocation?.name || NOT_AVAILABLE;
      let t39;
      if ($[130] !== referral.diagnosisList || $[131] !== referral.reasonForReferral) {
        t39 = [referral.reasonForReferral, ...((referral.diagnosisList ?? []) as DiagnosisItem[]).map(_temp32)].filter(_temp33).filter(_temp34).join(", ") || NOT_AVAILABLE;
        $[130] = referral.diagnosisList;
        $[131] = referral.reasonForReferral;
        $[132] = t39;
      } else {
        t39 = $[132];
      }
      const diagnosis = t39;
      let t40;
      if ($[133] !== referral) {
        t40 = describeTprSpecialtyCategories(matchTprDiagnosisSpecialties(referral)) || "None matched";
        $[133] = referral;
        $[134] = t40;
      } else {
        t40 = $[134];
      }
      const diagnosisCategories = t40;
      let t41;
      if ($[135] !== patient) {
        t41 = patient ? getPatientName(patient) || NOT_AVAILABLE : NOT_AVAILABLE;
        $[135] = patient;
        $[136] = t41;
      } else {
        t41 = $[136];
      }
      let t42;
      if ($[137] !== patient?.dateOfBirth) {
        t42 = formatDateMmDdYyyy(patient?.dateOfBirth) || NOT_AVAILABLE;
        $[137] = patient?.dateOfBirth;
        $[138] = t42;
      } else {
        t42 = $[138];
      }
      const t43 = homePhone ?? NOT_AVAILABLE;
      const t44 = mobilePhone ?? NOT_AVAILABLE;
      const t45 = workPhone ?? NOT_AVAILABLE;
      let t46;
      if ($[139] !== patient) {
        t46 = patient && getPatientFaxNumber(patient) || NOT_AVAILABLE;
        $[139] = patient;
        $[140] = t46;
      } else {
        t46 = $[140];
      }
      const t47 = patient?.ethnicity || NOT_AVAILABLE;
      const t48 = patient?.race || NOT_AVAILABLE;
      const t49 = patient?.language || NOT_AVAILABLE;
      const t50 = email ?? NOT_AVAILABLE;
      const t51 = syncedAddressValue || NOT_AVAILABLE;
      const t52 = referral.receivingProvider?.npiNumber || NOT_AVAILABLE;
      const t53 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      const t54 = referral.referringLocation?.name || NOT_AVAILABLE;
      let t55;
      if ($[141] !== referral.primaryCoverage) {
        t55 = referral.primaryCoverage ? getCoverageDisplayName({
          coverage: referral.primaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : "Self Pay";
        $[141] = referral.primaryCoverage;
        $[142] = t55;
      } else {
        t55 = $[142];
      }
      const t56 = referral.primaryCoverage?.subscriberId || NOT_AVAILABLE;
      let t57;
      if ($[143] !== referral.primaryCoverage) {
        t57 = getPolicyHolder(referral.primaryCoverage) || NOT_AVAILABLE;
        $[143] = referral.primaryCoverage;
        $[144] = t57;
      } else {
        t57 = $[144];
      }
      const t58 = referral.primaryCoverage?.payer;
      let t59;
      if ($[145] !== t58) {
        t59 = isTprSpecialCaseInsurance(t58);
        $[145] = t58;
        $[146] = t59;
      } else {
        t59 = $[146];
      }
      const t60 = coverageSlots.primary;
      const t61 = coverageSlots.secondary;
      let t62;
      if ($[147] !== referral.secondaryCoverage) {
        t62 = referral.secondaryCoverage ? getCoverageDisplayName({
          coverage: referral.secondaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : null;
        $[147] = referral.secondaryCoverage;
        $[148] = t62;
      } else {
        t62 = $[148];
      }
      const t63 = referral.secondaryCoverage?.subscriberId || null;
      let t64;
      if ($[149] !== referral.secondaryCoverage) {
        t64 = getPolicyHolder(referral.secondaryCoverage);
        $[149] = referral.secondaryCoverage;
        $[150] = t64;
      } else {
        t64 = $[150];
      }
      const t65 = referral.secondaryCoverage?.payer;
      let t66;
      if ($[151] !== t65) {
        t66 = isTprSpecialCaseInsurance(t65);
        $[151] = t65;
        $[152] = t66;
      } else {
        t66 = $[152];
      }
      let t67;
      if ($[153] !== referral.authorizationNumber) {
        t67 = referral.authorizationNumber?.trim() || NOT_AVAILABLE;
        $[153] = referral.authorizationNumber;
        $[154] = t67;
      } else {
        t67 = $[154];
      }
      let t68;
      if ($[155] !== referral.authorizationStatus) {
        t68 = referral.authorizationStatus ? formatFromUnderscoreToHumanReadable(referral.authorizationStatus) : NOT_AVAILABLE;
        $[155] = referral.authorizationStatus;
        $[156] = t68;
      } else {
        t68 = $[156];
      }
      let t69;
      if ($[157] !== referral.startDate) {
        t69 = formatDateMmDdYyyy(referral.startDate) || NOT_AVAILABLE;
        $[157] = referral.startDate;
        $[158] = t69;
      } else {
        t69 = $[158];
      }
      let t70;
      if ($[159] !== referral.endDate) {
        t70 = formatDateMmDdYyyy(referral.endDate) || NOT_AVAILABLE;
        $[159] = referral.endDate;
        $[160] = t70;
      } else {
        t70 = $[160];
      }
      let t71;
      if ($[161] !== referral.numberOfVisits) {
        t71 = referral.numberOfVisits?.toString() ?? NOT_AVAILABLE;
        $[161] = referral.numberOfVisits;
        $[162] = t71;
      } else {
        t71 = $[162];
      }
      let t72;
      if ($[163] !== referral.numberOfServices) {
        t72 = referral.numberOfServices?.toString() ?? null;
        $[163] = referral.numberOfServices;
        $[164] = t72;
      } else {
        t72 = $[164];
      }
      let t73;
      if ($[165] !== coverageSlots.primary || $[166] !== coverageSlots.secondary || $[167] !== dateReceived || $[168] !== diagnosis || $[169] !== diagnosisCategories || $[170] !== emergencyContactName || $[171] !== emergencyContactPhoneNumber || $[172] !== emergencyContactRelationship || $[173] !== receivingProviderName || $[174] !== referringLocationAddress || $[175] !== referringProviderName || $[176] !== referringProviderSpecialty || $[177] !== t41 || $[178] !== t42 || $[179] !== t43 || $[180] !== t44 || $[181] !== t45 || $[182] !== t46 || $[183] !== t47 || $[184] !== t48 || $[185] !== t49 || $[186] !== t50 || $[187] !== t51 || $[188] !== t52 || $[189] !== t53 || $[190] !== t54 || $[191] !== t55 || $[192] !== t56 || $[193] !== t57 || $[194] !== t59 || $[195] !== t62 || $[196] !== t63 || $[197] !== t64 || $[198] !== t66 || $[199] !== t67 || $[200] !== t68 || $[201] !== t69 || $[202] !== t70 || $[203] !== t71 || $[204] !== t72) {
        t73 = {
          patientName: t41,
          patientDateOfBirth: t42,
          patientHomePhoneNumber: t43,
          patientMobilePhoneNumber: t44,
          patientWorkPhoneNumber: t45,
          patientFaxNumber: t46,
          patientEthnicity: t47,
          patientRace: t48,
          patientLanguage: t49,
          patientEmail: t50,
          patientHomeAddress: t51,
          receivingProviderName,
          receivingProviderNpi: t52,
          referringProviderName,
          referringProviderNpi: t53,
          referringProviderSpecialty,
          referringLocationName: t54,
          referringLocationAddress,
          emergencyContactName,
          emergencyContactPhoneNumber,
          emergencyContactRelationship,
          dateReceived,
          primaryPlanLabel: t55,
          primarySubscriberId: t56,
          primaryPolicyHolder: t57,
          isSpecialCaseInsurance: t59,
          primaryCoverageSlot: t60,
          secondaryCoverageSlot: t61,
          secondaryPlanLabel: t62,
          secondarySubscriberId: t63,
          secondaryPolicyHolder: t64,
          isSecondarySpecialCaseInsurance: t66,
          authorizationNumber: t67,
          authorizationStatus: t68,
          authorizationStartDate: t69,
          authorizationEndDate: t70,
          authorizationVisits: t71,
          authorizationServices: t72,
          diagnosis,
          diagnosisCategories
        };
        $[165] = coverageSlots.primary;
        $[166] = coverageSlots.secondary;
        $[167] = dateReceived;
        $[168] = diagnosis;
        $[169] = diagnosisCategories;
        $[170] = emergencyContactName;
        $[171] = emergencyContactPhoneNumber;
        $[172] = emergencyContactRelationship;
        $[173] = receivingProviderName;
        $[174] = referringLocationAddress;
        $[175] = referringProviderName;
        $[176] = referringProviderSpecialty;
        $[177] = t41;
        $[178] = t42;
        $[179] = t43;
        $[180] = t44;
        $[181] = t45;
        $[182] = t46;
        $[183] = t47;
        $[184] = t48;
        $[185] = t49;
        $[186] = t50;
        $[187] = t51;
        $[188] = t52;
        $[189] = t53;
        $[190] = t54;
        $[191] = t55;
        $[192] = t56;
        $[193] = t57;
        $[194] = t59;
        $[195] = t62;
        $[196] = t63;
        $[197] = t64;
        $[198] = t66;
        $[199] = t67;
        $[200] = t68;
        $[201] = t69;
        $[202] = t70;
        $[203] = t71;
        $[204] = t72;
        $[205] = t73;
      } else {
        t73 = $[205];
      }
      const opsCrossCheck = t73;
      let t74;
      let t75;
      if ($[206] !== athenaData?.coverages) {
        const sortedAthenaCoverages = [...(athenaData?.coverages ?? [])].sort(_temp35);
        t74 = sortedAthenaCoverages.find(_temp36) ?? sortedAthenaCoverages[0] ?? null;
        t75 = sortedAthenaCoverages.find(_temp37) ?? sortedAthenaCoverages[1] ?? null;
        $[206] = athenaData?.coverages;
        $[207] = t74;
        $[208] = t75;
      } else {
        t74 = $[207];
        t75 = $[208];
      }
      let t76;
      if ($[209] !== t74 || $[210] !== t75) {
        t76 = {
          primary: t74,
          secondary: t75
        };
        $[209] = t74;
        $[210] = t75;
        $[211] = t76;
      } else {
        t76 = $[211];
      }
      const athenaCoverageLive = t76;
      if (view === "steps") {
        let t77;
        if ($[212] !== actionNote || $[213] !== athenaCoverageLive || $[214] !== bucketLabel || $[215] !== chartReferralAuthDocuments || $[216] !== chartReferralAuths || $[217] !== chartRequestLetters || $[218] !== coverageReadback || $[219] !== decisionTrace || $[220] !== documentId || $[221] !== documentUploads || $[222] !== followThroughEligible || $[223] !== hasMissingInformation || $[224] !== insuranceRequirements || $[225] !== insuranceStatusDisplay || $[226] !== internalNoteToEnter || $[227] !== isPrimaryCareProvider || $[228] !== isReferralAuth || $[229] !== opsCrossCheck || $[230] !== pastAppointments || $[231] !== patient.displayId || $[232] !== patientContactSyncGaps || $[233] !== patientEhrId || $[234] !== patientLabel || $[235] !== pendingApproval || $[236] !== primaryCareProviderAlreadyPresent || $[237] !== privacyNoticeOnFile || $[238] !== receivingAssignmentConfirmed || $[239] !== referral.id || $[240] !== referralAuthorization || $[241] !== referralSourceOnFile || $[242] !== referringProviderMarkedPcp || $[243] !== resolvedReceiving || $[244] !== task || $[245] !== taskSteps || $[246] !== unresolvedUpcomingProviders || $[247] !== upcomingAppointmentProviderSummary || $[248] !== upcomingAppointments || $[249] !== urgency) {
          t77 = <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto"><TprSyncTimeline task={task} referralId={referral.id} taskSteps={taskSteps} pendingApproval={pendingApproval} receivingAssignmentConfirmed={receivingAssignmentConfirmed} followThroughEligible={followThroughEligible} coverageReadback={coverageReadback} athenaCoverageLive={athenaCoverageLive} insuranceRequirements={insuranceRequirements} referralAuthorization={referralAuthorization} resolvedReceiving={resolvedReceiving} patientLabel={patientLabel} patientEhrId={patientEhrId} documentId={documentId} bucketLabel={bucketLabel} internalNoteToEnter={internalNoteToEnter} urgency={urgency} actionNote={actionNote} decisionTrace={decisionTrace} insuranceStatusDisplay={insuranceStatusDisplay} hasMissingInformation={hasMissingInformation} unresolvedUpcomingProviders={unresolvedUpcomingProviders} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} upcomingAppointments={upcomingAppointments} pastAppointments={pastAppointments} documentUploads={documentUploads} privacyNoticeOnFile={privacyNoticeOnFile} referralSourceOnFile={referralSourceOnFile} isPrimaryCareProvider={isPrimaryCareProvider} referringProviderMarkedPcp={referringProviderMarkedPcp} primaryCareProviderAlreadyPresent={primaryCareProviderAlreadyPresent} patientDisplayId={patient.displayId} opsCrossCheck={opsCrossCheck} patientContactSyncGaps={patientContactSyncGaps} chartReferralAuths={chartReferralAuths} chartRequestLetters={chartRequestLetters} isReferralAuth={isReferralAuth} chartReferralAuthDocuments={chartReferralAuthDocuments} /></div>;
          $[212] = actionNote;
          $[213] = athenaCoverageLive;
          $[214] = bucketLabel;
          $[215] = chartReferralAuthDocuments;
          $[216] = chartReferralAuths;
          $[217] = chartRequestLetters;
          $[218] = coverageReadback;
          $[219] = decisionTrace;
          $[220] = documentId;
          $[221] = documentUploads;
          $[222] = followThroughEligible;
          $[223] = hasMissingInformation;
          $[224] = insuranceRequirements;
          $[225] = insuranceStatusDisplay;
          $[226] = internalNoteToEnter;
          $[227] = isPrimaryCareProvider;
          $[228] = isReferralAuth;
          $[229] = opsCrossCheck;
          $[230] = pastAppointments;
          $[231] = patient.displayId;
          $[232] = patientContactSyncGaps;
          $[233] = patientEhrId;
          $[234] = patientLabel;
          $[235] = pendingApproval;
          $[236] = primaryCareProviderAlreadyPresent;
          $[237] = privacyNoticeOnFile;
          $[238] = receivingAssignmentConfirmed;
          $[239] = referral.id;
          $[240] = referralAuthorization;
          $[241] = referralSourceOnFile;
          $[242] = referringProviderMarkedPcp;
          $[243] = resolvedReceiving;
          $[244] = task;
          $[245] = taskSteps;
          $[246] = unresolvedUpcomingProviders;
          $[247] = upcomingAppointmentProviderSummary;
          $[248] = upcomingAppointments;
          $[249] = urgency;
          $[250] = t77;
        } else {
          t77 = $[250];
        }
        t26 = t77;
        break bb0;
      }
      t25 = "flex flex-col gap-8 sm:min-w-[800px] w-full max-w-2xl mx-auto";
      T0 = EhrInstructionsCard;
      t24 = "Detailed Athena verification reference";
      t17 = "text-sm text-muted-foreground space-y-6";
      if ($[251] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = <h3 className="text-md font-semibold text-center text-foreground">TPR Biltmore Cardiology Sync Verification</h3>;
        $[251] = t18;
      } else {
        t18 = $[251];
      }
      const t77 = syncFailed ? "Automation did not complete for this task. Resolve the sync error below and verify Athena before closing." : "Automation has already run for this task. Use this checklist to verify the sync in Athena before closing.";
      if ($[252] !== t77) {
        t19 = <p className="text-xs text-center">{t77}</p>;
        $[252] = t77;
        $[253] = t19;
      } else {
        t19 = $[253];
      }
      if ($[254] !== task.id) {
        t20 = <SyncIncompleteActions taskId={task.id} />;
        $[254] = task.id;
        $[255] = t20;
      } else {
        t20 = $[255];
      }
      let t78;
      if ($[256] === Symbol.for("react.memo_cache_sentinel")) {
        t78 = <strong>Camelback Referral:</strong>;
        $[256] = t78;
      } else {
        t78 = $[256];
      }
      const t79 = isCamelbackReferral ? "Yes" : "No";
      let t80;
      if ($[257] !== t79) {
        t80 = <p>{t78} {t79}</p>;
        $[257] = t79;
        $[258] = t80;
      } else {
        t80 = $[258];
      }
      let t81;
      if ($[259] === Symbol.for("react.memo_cache_sentinel")) {
        t81 = <strong>Patient:</strong>;
        $[259] = t81;
      } else {
        t81 = $[259];
      }
      let t82;
      if ($[260] !== patientDisplayName) {
        t82 = <CopyableContent text={patientDisplayName} />;
        $[260] = patientDisplayName;
        $[261] = t82;
      } else {
        t82 = $[261];
      }
      let t83;
      if ($[262] !== patientLabel || $[263] !== t82) {
        t83 = <p>{t81} {t82} ({patientLabel})</p>;
        $[262] = patientLabel;
        $[263] = t82;
        $[264] = t83;
      } else {
        t83 = $[264];
      }
      let t84;
      if ($[265] === Symbol.for("react.memo_cache_sentinel")) {
        t84 = <strong>Receiving Provider (as extracted):</strong>;
        $[265] = t84;
      } else {
        t84 = $[265];
      }
      let t85;
      if ($[266] !== referral.receivingProvider) {
        t85 = <ProviderCopyFields provider={referral.receivingProvider} />;
        $[266] = referral.receivingProvider;
        $[267] = t85;
      } else {
        t85 = $[267];
      }
      let t86;
      if ($[268] === Symbol.for("react.memo_cache_sentinel")) {
        t86 = <strong>Location:</strong>;
        $[268] = t86;
      } else {
        t86 = $[268];
      }
      let t87;
      if ($[269] !== receivingLocationName) {
        t87 = <CopyableContent text={receivingLocationName} />;
        $[269] = receivingLocationName;
        $[270] = t87;
      } else {
        t87 = $[270];
      }
      let t88;
      if ($[271] !== t85 || $[272] !== t87) {
        t88 = <p>{t84}{" "}{t85}, {t86}{" "}{t87}</p>;
        $[271] = t85;
        $[272] = t87;
        $[273] = t88;
      } else {
        t88 = $[273];
      }
      if ($[274] !== t80 || $[275] !== t83 || $[276] !== t88) {
        t21 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t80}{t83}{t88}</div>;
        $[274] = t80;
        $[275] = t83;
        $[276] = t88;
        $[277] = t21;
      } else {
        t21 = $[277];
      }
      let t89;
      let t90;
      if ($[278] === Symbol.for("react.memo_cache_sentinel")) {
        t89 = <p className="font-semibold">Recommended Receiving Provider / Location</p>;
        t90 = <strong>Provider:</strong>;
        $[278] = t89;
        $[279] = t90;
      } else {
        t89 = $[278];
        t90 = $[279];
      }
      const t91 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
      let t92;
      if ($[280] !== t91) {
        t92 = <CopyableContent text={t91} />;
        $[280] = t91;
        $[281] = t92;
      } else {
        t92 = $[281];
      }
      let t93;
      if ($[282] === Symbol.for("react.memo_cache_sentinel")) {
        t93 = <strong>Location:</strong>;
        $[282] = t93;
      } else {
        t93 = $[282];
      }
      const t94 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
      let t95;
      if ($[283] !== t94) {
        t95 = <CopyableContent text={t94} />;
        $[283] = t94;
        $[284] = t95;
      } else {
        t95 = $[284];
      }
      let t96;
      if ($[285] !== t92 || $[286] !== t95) {
        t96 = <p>{t90} {t92},{" "}{t93} {t95}</p>;
        $[285] = t92;
        $[286] = t95;
        $[287] = t96;
      } else {
        t96 = $[287];
      }
      let t97;
      if ($[288] !== resolvedReceiving.routedToSscardio) {
        t97 = resolvedReceiving.routedToSscardio && <p className="text-xs">Routing: <strong>sscardio</strong></p>;
        $[288] = resolvedReceiving.routedToSscardio;
        $[289] = t97;
      } else {
        t97 = $[289];
      }
      let t98;
      if ($[290] !== resolvedReceiving.flags) {
        t98 = resolvedReceiving.flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{resolvedReceiving.flags.map(_temp38)}</ul>;
        $[290] = resolvedReceiving.flags;
        $[291] = t98;
      } else {
        t98 = $[291];
      }
      let t99;
      if ($[292] !== resolvedReceiving.trace) {
        t99 = resolvedReceiving.trace.length > 0 && <details className="text-xs"><summary>Why</summary><ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{resolvedReceiving.trace.map(_temp39)}</ul></details>;
        $[292] = resolvedReceiving.trace;
        $[293] = t99;
      } else {
        t99 = $[293];
      }
      if ($[294] !== t96 || $[295] !== t97 || $[296] !== t98 || $[297] !== t99) {
        t22 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t89}{t96}{t97}{t98}{t99}</div>;
        $[294] = t96;
        $[295] = t97;
        $[296] = t98;
        $[297] = t99;
        $[298] = t22;
      } else {
        t22 = $[298];
      }
      let t100;
      if ($[299] === Symbol.for("react.memo_cache_sentinel")) {
        t100 = <h4 className="font-semibold text-foreground mb-2">Patient Chart Create</h4>;
        $[299] = t100;
      } else {
        t100 = $[299];
      }
      let t101;
      if ($[300] !== documentId) {
        t101 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[300] = documentId;
        $[301] = t101;
      } else {
        t101 = $[301];
      }
      let t102;
      if ($[302] !== patientEhrId) {
        t102 = <li>Add patient EHR ID if not already added: <CopyableContent text={patientEhrId} /></li>;
        $[302] = patientEhrId;
        $[303] = t102;
      } else {
        t102 = $[303];
      }
      let t103;
      if ($[304] === Symbol.for("react.memo_cache_sentinel")) {
        t103 = <li>Hit save and proceed to quickview.</li>;
        $[304] = t103;
      } else {
        t103 = $[304];
      }
      let t104;
      if ($[305] !== resolvedReceiving.providerLabel || $[306] !== resolvedReceiving.providerNpi || $[307] !== resolvedReceiving.usualProviderWriteSafe) {
        t104 = resolvedReceiving.usualProviderWriteSafe ? <ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={true} /> : "Leave unchanged";
        $[305] = resolvedReceiving.providerLabel;
        $[306] = resolvedReceiving.providerNpi;
        $[307] = resolvedReceiving.usualProviderWriteSafe;
        $[308] = t104;
      } else {
        t104 = $[308];
      }
      const t105 = resolvedReceiving.usualProviderWriteSafe ? "New patient \u2014 add the usual provider to the care team." : "Established patient \u2014 left unchanged to preserve continuity of care.";
      let t106;
      if ($[309] !== t105) {
        t106 = <p className="text-xs italic mt-1">{t105}</p>;
        $[309] = t105;
        $[310] = t106;
      } else {
        t106 = $[310];
      }
      let t107;
      if ($[311] !== t104 || $[312] !== t106) {
        t107 = <li>Usual Provider:{" "}{t104}{t106}</li>;
        $[311] = t104;
        $[312] = t106;
        $[313] = t107;
      } else {
        t107 = $[313];
      }
      let t108;
      if ($[314] !== referral.referringProvider) {
        t108 = <ProviderCopyFields provider={referral.referringProvider} />;
        $[314] = referral.referringProvider;
        $[315] = t108;
      } else {
        t108 = $[315];
      }
      let t109;
      if ($[316] !== referringProviderSpecialty) {
        t109 = <CopyableContent text={referringProviderSpecialty} />;
        $[316] = referringProviderSpecialty;
        $[317] = t109;
      } else {
        t109 = $[317];
      }
      let t110;
      if ($[318] !== t108 || $[319] !== t109) {
        t110 = <li>Referring Provider: {t108}, Specialty:{" "}{t109}</li>;
        $[318] = t108;
        $[319] = t109;
        $[320] = t110;
      } else {
        t110 = $[320];
      }
      let t111;
      if ($[321] !== isPrimaryCareProvider || $[322] !== referral.referringProvider || $[323] !== referringProviderMarkedPcp) {
        t111 = (isPrimaryCareProvider || referringProviderMarkedPcp) && <li>PCP: <ProviderCopyFields provider={referral.referringProvider} /></li>;
        $[321] = isPrimaryCareProvider;
        $[322] = referral.referringProvider;
        $[323] = referringProviderMarkedPcp;
        $[324] = t111;
      } else {
        t111 = $[324];
      }
      let t112;
      if ($[325] !== referral.referringProvider || $[326] !== referringLocationAddress) {
        t112 = referral.referringProvider && <li>Referring provider address: <CopyableContent text={referringLocationAddress} /></li>;
        $[325] = referral.referringProvider;
        $[326] = referringLocationAddress;
        $[327] = t112;
      } else {
        t112 = $[327];
      }
      let t113;
      if ($[328] !== t107 || $[329] !== t110 || $[330] !== t111 || $[331] !== t112) {
        t113 = <li>Check the Patient Care Team:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t107}{t110}{t111}{t112}</ul></li>;
        $[328] = t107;
        $[329] = t110;
        $[330] = t111;
        $[331] = t112;
        $[332] = t113;
      } else {
        t113 = $[332];
      }
      let t114;
      if ($[333] !== emergencyContactName) {
        t114 = <li>Name: <CopyableContent text={emergencyContactName} /></li>;
        $[333] = emergencyContactName;
        $[334] = t114;
      } else {
        t114 = $[334];
      }
      let t115;
      if ($[335] !== emergencyContactPhoneNumber) {
        t115 = <li>Phone Number: <CopyableContent text={emergencyContactPhoneNumber} /></li>;
        $[335] = emergencyContactPhoneNumber;
        $[336] = t115;
      } else {
        t115 = $[336];
      }
      let t116;
      if ($[337] !== emergencyContactRelationship) {
        t116 = <li>Relationship to Patient: <CopyableContent text={emergencyContactRelationship} /></li>;
        $[337] = emergencyContactRelationship;
        $[338] = t116;
      } else {
        t116 = $[338];
      }
      let t117;
      if ($[339] !== t114 || $[340] !== t115 || $[341] !== t116) {
        t117 = <li>Check the Emergency Contact<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t114}{t115}{t116}</ul></li>;
        $[339] = t114;
        $[340] = t115;
        $[341] = t116;
        $[342] = t117;
      } else {
        t117 = $[342];
      }
      const t118 = primaryCoverageSummary ?? "N/A";
      let t119;
      if ($[343] !== t118) {
        t119 = <CopyableContent text={t118} />;
        $[343] = t118;
        $[344] = t119;
      } else {
        t119 = $[344];
      }
      let t120;
      if ($[345] !== coverageSlots.primary.label || $[346] !== t119) {
        t120 = <li>{coverageSlots.primary.label}: {t119}</li>;
        $[345] = coverageSlots.primary.label;
        $[346] = t119;
        $[347] = t120;
      } else {
        t120 = $[347];
      }
      let t121;
      if ($[348] !== coverageSlots.secondary || $[349] !== secondaryCoverageSummary) {
        t121 = secondaryCoverageSummary && <li>{coverageSlots.secondary.label}: <CopyableContent text={secondaryCoverageSummary} /></li>;
        $[348] = coverageSlots.secondary;
        $[349] = secondaryCoverageSummary;
        $[350] = t121;
      } else {
        t121 = $[350];
      }
      let t122;
      let t123;
      if ($[351] !== insuranceStatusDisplay) {
        t122 = <CopyableContent text={insuranceStatusDisplay} />;
        t123 = insuranceStatusDisplay === "Missing Insurance" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[351] = insuranceStatusDisplay;
        $[352] = t122;
        $[353] = t123;
      } else {
        t122 = $[352];
        t123 = $[353];
      }
      let t124;
      if ($[354] !== insuranceStatusDisplay) {
        t124 = insuranceStatusDisplay === "Unverified" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[354] = insuranceStatusDisplay;
        $[355] = t124;
      } else {
        t124 = $[355];
      }
      let t125;
      if ($[356] !== t122 || $[357] !== t123 || $[358] !== t124) {
        t125 = <li>Eligibility check result: {t122}{t123}{t124}</li>;
        $[356] = t122;
        $[357] = t123;
        $[358] = t124;
        $[359] = t125;
      } else {
        t125 = $[359];
      }
      let t126;
      if ($[360] !== t120 || $[361] !== t121 || $[362] !== t125) {
        t126 = <li>Check the patient insurance<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t120}{t121}{t125}</ul></li>;
        $[360] = t120;
        $[361] = t121;
        $[362] = t125;
        $[363] = t126;
      } else {
        t126 = $[363];
      }
      let t127;
      if ($[364] === Symbol.for("react.memo_cache_sentinel")) {
        t127 = <li>Call and text are YES</li>;
        $[364] = t127;
      } else {
        t127 = $[364];
      }
      let t128;
      if ($[365] !== privacyNoticeOnFile) {
        t128 = privacyNoticeOnFile !== true && <p className="text-xs italic mt-1">{privacyNoticeOnFile === false ? "Privacy is not set on the Athena chart. Set it to YES during review." : "Could not verify Athena privacy. Confirm it is YES during review."}</p>;
        $[365] = privacyNoticeOnFile;
        $[366] = t128;
      } else {
        t128 = $[366];
      }
      let t129;
      if ($[367] !== t128) {
        t129 = <li>Privacy is YES{t128}</li>;
        $[367] = t128;
        $[368] = t129;
      } else {
        t129 = $[368];
      }
      let t130;
      if ($[369] === Symbol.for("react.memo_cache_sentinel")) {
        t130 = <li>If How did you hear about us is blank, set it to PHYSICIAN REFERRAL; leave any existing value unchanged.</li>;
        $[369] = t130;
      } else {
        t130 = $[369];
      }
      let t131;
      if ($[370] !== t129) {
        t131 = <li>Go to full registration and verify:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t127}{t129}{t130}</ul></li>;
        $[370] = t129;
        $[371] = t131;
      } else {
        t131 = $[371];
      }
      if ($[372] !== t101 || $[373] !== t102 || $[374] !== t113 || $[375] !== t117 || $[376] !== t126 || $[377] !== t131) {
        t23 = <div>{t100}<ol className="list-decimal list-inside space-y-3">{t101}{t102}{t103}{t113}{t117}{t126}{t131}</ol></div>;
        $[372] = t101;
        $[373] = t102;
        $[374] = t113;
        $[375] = t117;
        $[376] = t126;
        $[377] = t131;
        $[378] = t23;
      } else {
        t23 = $[378];
      }
      if ($[379] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = <h4 className="font-semibold text-foreground mb-2">Referral Processing</h4>;
        $[379] = t16;
      } else {
        t16 = $[379];
      }
      t12 = "list-decimal list-inside space-y-3";
      if ($[380] !== documentId) {
        t13 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[380] = documentId;
        $[381] = t13;
      } else {
        t13 = $[381];
      }
      let t132;
      if ($[382] !== referringProviderName) {
        t132 = <CopyableContent text={referringProviderName} />;
        $[382] = referringProviderName;
        $[383] = t132;
      } else {
        t132 = $[383];
      }
      const t133 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      let t134;
      if ($[384] !== t133) {
        t134 = <CopyableContent text={t133} />;
        $[384] = t133;
        $[385] = t134;
      } else {
        t134 = $[385];
      }
      let t135;
      if ($[386] !== referringLocationAddress) {
        t135 = <CopyableContent text={referringLocationAddress} />;
        $[386] = referringLocationAddress;
        $[387] = t135;
      } else {
        t135 = $[387];
      }
      if ($[388] !== t132 || $[389] !== t134 || $[390] !== t135) {
        t14 = <li>Referring provider: Name: {t132}, NPI:{" "}{t134}, Address:{" "}{t135}</li>;
        $[388] = t132;
        $[389] = t134;
        $[390] = t135;
        $[391] = t14;
      } else {
        t14 = $[391];
      }
      if ($[392] !== resolvedReceiving.providerLabel || $[393] !== resolvedReceiving.providerNpi || $[394] !== resolvedReceiving.providerWriteSafe) {
        t15 = <li>Receiving provider:{" "}<ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={resolvedReceiving.providerWriteSafe} /></li>;
        $[392] = resolvedReceiving.providerLabel;
        $[393] = resolvedReceiving.providerNpi;
        $[394] = resolvedReceiving.providerWriteSafe;
        $[395] = t15;
      } else {
        t15 = $[395];
      }
      t10 = "Patient Type: ";
      if ($[396] !== patientLabel) {
        t11 = <CopyableContent text={patientLabel} />;
        $[396] = patientLabel;
        $[397] = t11;
      } else {
        t11 = $[397];
      }
      t8 = "ml-4 mt-1 space-y-1 list-disc list-inside text-xs";
      t9 = sortedPastAppointments.length === 0 ? <li>No appointment history found</li> : sortedPastAppointments.map(_temp40);
    }
    $[15] = actionNote;
    $[16] = athenaData?.coverages;
    $[17] = athenaData?.patientDetails;
    $[18] = bucketLabel;
    $[19] = chartReferralAuthDocuments;
    $[20] = chartReferralAuths;
    $[21] = chartRequestLetters;
    $[22] = coverageReadback;
    $[23] = coverageSlots.primary;
    $[24] = coverageSlots.secondary;
    $[25] = dateReceived;
    $[26] = decisionTrace;
    $[27] = documentId;
    $[28] = documentUploads;
    $[29] = eligibilityDisplay;
    $[30] = emergencyContactName;
    $[31] = emergencyContactPhoneNumber;
    $[32] = emergencyContactRelationship;
    $[33] = followThroughEligible;
    $[34] = insuranceRequirements;
    $[35] = internalNoteToEnter;
    $[36] = isPrimaryCareProvider;
    $[37] = isReferralAuth;
    $[38] = pastAppointments;
    $[39] = patient;
    $[40] = patientEhrId;
    $[41] = patientLabel;
    $[42] = pendingApproval;
    $[43] = primaryCareProviderAlreadyPresent;
    $[44] = primaryCoverageSummary;
    $[45] = privacyNoticeOnFile;
    $[46] = receivingAssignmentConfirmed;
    $[47] = receivingProviderName;
    $[48] = referral;
    $[49] = referralAuthorization;
    $[50] = referralSourceOnFile;
    $[51] = referringLocationAddress;
    $[52] = referringProviderMarkedPcp;
    $[53] = referringProviderName;
    $[54] = referringProviderSpecialty;
    $[55] = resolvedReceiving;
    $[56] = secondaryCoverageSummary;
    $[57] = task;
    $[58] = taskSteps;
    $[59] = unresolvedUpcomingProviders;
    $[60] = upcomingAppointmentProviderSummary;
    $[61] = upcomingAppointments;
    $[62] = urgency;
    $[63] = view;
    $[64] = T0;
    $[65] = dataSourceEntries;
    $[66] = hasMissingInformation;
    $[67] = missingFields;
    $[68] = t10;
    $[69] = t11;
    $[70] = t12;
    $[71] = t13;
    $[72] = t14;
    $[73] = t15;
    $[74] = t16;
    $[75] = t17;
    $[76] = t18;
    $[77] = t19;
    $[78] = t20;
    $[79] = t21;
    $[80] = t22;
    $[81] = t23;
    $[82] = t24;
    $[83] = t25;
    $[84] = t26;
    $[85] = t8;
    $[86] = t9;
  } else {
    T0 = $[64];
    dataSourceEntries = $[65];
    hasMissingInformation = $[66];
    missingFields = $[67];
    t10 = $[68];
    t11 = $[69];
    t12 = $[70];
    t13 = $[71];
    t14 = $[72];
    t15 = $[73];
    t16 = $[74];
    t17 = $[75];
    t18 = $[76];
    t19 = $[77];
    t20 = $[78];
    t21 = $[79];
    t22 = $[80];
    t23 = $[81];
    t24 = $[82];
    t25 = $[83];
    t26 = $[84];
    t8 = $[85];
    t9 = $[86];
  }
  if (t26 !== Symbol.for("react.early_return_sentinel")) {
    return t26;
  }
  let t27;
  if ($[398] !== t8 || $[399] !== t9) {
    t27 = <ul className={t8}>{t9}</ul>;
    $[398] = t8;
    $[399] = t9;
    $[400] = t27;
  } else {
    t27 = $[400];
  }
  let t28;
  if ($[401] !== t10 || $[402] !== t11 || $[403] !== t27) {
    t28 = <li>{t10}{t11}{t27}</li>;
    $[401] = t10;
    $[402] = t11;
    $[403] = t27;
    $[404] = t28;
  } else {
    t28 = $[404];
  }
  let t29;
  if ($[405] !== dateReceived) {
    t29 = <li>Date: <CopyableContent text={dateReceived} /></li>;
    $[405] = dateReceived;
    $[406] = t29;
  } else {
    t29 = $[406];
  }
  let t30;
  if ($[407] !== internalNoteToEnter) {
    t30 = <CopyableContent text={internalNoteToEnter} />;
    $[407] = internalNoteToEnter;
    $[408] = t30;
  } else {
    t30 = $[408];
  }
  let t31;
  if ($[409] !== flags) {
    t31 = flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{flags.map(_temp41)}</ul>;
    $[409] = flags;
    $[410] = t31;
  } else {
    t31 = $[410];
  }
  let t32;
  if ($[411] !== t30 || $[412] !== t31) {
    t32 = <li>Internal Note: {t30}{t31}</li>;
    $[411] = t30;
    $[412] = t31;
    $[413] = t32;
  } else {
    t32 = $[413];
  }
  let t33;
  if ($[414] !== schedulingPriority) {
    t33 = <li>Urgency: <CopyableContent text={schedulingPriority} /></li>;
    $[414] = schedulingPriority;
    $[415] = t33;
  } else {
    t33 = $[415];
  }
  let t34;
  if ($[416] !== bucketLabel) {
    t34 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <>{NO_CONFIDENT_BUCKET_SUGGESTION}</> : <>Suggested Bucket: <CopyableContent text={SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} /></>;
    $[416] = bucketLabel;
    $[417] = t34;
  } else {
    t34 = $[417];
  }
  let t35;
  if ($[418] !== bucketLabel || $[419] !== decisionTrace) {
    t35 = decisionTrace.length > 0 && (bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{decisionTrace.map(_temp42)}</ul> : <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs"><li>{decisionTrace[decisionTrace.length - 1].label}{" "}<strong>{decisionTrace[decisionTrace.length - 1].outcome}</strong></li></ul>);
    $[418] = bucketLabel;
    $[419] = decisionTrace;
    $[420] = t35;
  } else {
    t35 = $[420];
  }
  let t36;
  if ($[421] !== t34 || $[422] !== t35) {
    t36 = <li>{t34}{t35}</li>;
    $[421] = t34;
    $[422] = t35;
    $[423] = t36;
  } else {
    t36 = $[423];
  }
  let t37;
  if ($[424] !== actionNote) {
    t37 = <li>Action note: <CopyableContent text={actionNote} /></li>;
    $[424] = actionNote;
    $[425] = t37;
  } else {
    t37 = $[425];
  }
  let t38;
  if ($[426] !== internalNoteToEnter) {
    t38 = <li>Go to quickview and add the internal note: <CopyableContent text={internalNoteToEnter} /></li>;
    $[426] = internalNoteToEnter;
    $[427] = t38;
  } else {
    t38 = $[427];
  }
  let t39;
  if ($[428] !== t12 || $[429] !== t13 || $[430] !== t14 || $[431] !== t15 || $[432] !== t28 || $[433] !== t29 || $[434] !== t32 || $[435] !== t33 || $[436] !== t36 || $[437] !== t37 || $[438] !== t38) {
    t39 = <ol className={t12}>{t13}{t14}{t15}{t28}{t29}{t32}{t33}{t36}{t37}{t38}</ol>;
    $[428] = t12;
    $[429] = t13;
    $[430] = t14;
    $[431] = t15;
    $[432] = t28;
    $[433] = t29;
    $[434] = t32;
    $[435] = t33;
    $[436] = t36;
    $[437] = t37;
    $[438] = t38;
    $[439] = t39;
  } else {
    t39 = $[439];
  }
  let t40;
  if ($[440] !== t16 || $[441] !== t39) {
    t40 = <div>{t16}{t39}</div>;
    $[440] = t16;
    $[441] = t39;
    $[442] = t40;
  } else {
    t40 = $[442];
  }
  let t41;
  let t42;
  if ($[443] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <h4 className="font-semibold text-foreground mb-2">Doc Upload</h4>;
    t42 = <p className="mb-1">Verify the following documents were uploaded, and add the listed document label to each lab and imaging result so it can be closed:</p>;
    $[443] = t41;
    $[444] = t42;
  } else {
    t41 = $[443];
    t42 = $[444];
  }
  let t43;
  if ($[445] !== documentUploads) {
    t43 = documentUploads.length === 0 ? <li>N/A</li> : documentUploads.map(_temp43);
    $[445] = documentUploads;
    $[446] = t43;
  } else {
    t43 = $[446];
  }
  let t44;
  if ($[447] !== t43) {
    t44 = <div>{t41}{t42}<ul className="ml-4 space-y-1 list-disc list-inside">{t43}</ul></div>;
    $[447] = t43;
    $[448] = t44;
  } else {
    t44 = $[448];
  }
  let t45;
  let t46;
  if ($[449] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <h4 className="font-semibold text-foreground mb-2">Missing Information</h4>;
    t46 = <p className="font-medium text-foreground">Step 1: Missing Information Check</p>;
    $[449] = t45;
    $[450] = t46;
  } else {
    t45 = $[449];
    t46 = $[450];
  }
  let t47;
  if ($[451] !== hasMissingInformation || $[452] !== missingFields) {
    t47 = hasMissingInformation ? `Missing: ${missingFields.map(_temp44).join(", ")}` : "No missing-information letter needed.";
    $[451] = hasMissingInformation;
    $[452] = missingFields;
    $[453] = t47;
  } else {
    t47 = $[453];
  }
  let t48;
  if ($[454] !== t47) {
    t48 = <p className="mb-3">{t47}</p>;
    $[454] = t47;
    $[455] = t48;
  } else {
    t48 = $[455];
  }
  let t49;
  if ($[456] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <p className="font-medium text-foreground">Step 2: Data Source Checklist</p>;
    $[456] = t49;
  } else {
    t49 = $[456];
  }
  let t50;
  if ($[457] !== dataSourceEntries) {
    t50 = <ul className="ml-4 mb-3 space-y-1 list-disc list-inside">{dataSourceEntries.map(_temp45)}</ul>;
    $[457] = dataSourceEntries;
    $[458] = t50;
  } else {
    t50 = $[458];
  }
  let t51;
  if ($[459] !== dateReceived || $[460] !== hasMissingInformation || $[461] !== referringProviderName) {
    t51 = hasMissingInformation && <ol className="list-decimal list-inside space-y-2"><li>Verify the Auto-Generated document was added to the patient chart</li><li>Send the document out.<ul className="ml-4 mt-1 space-y-2 list-none"><li>a. Chart Export - sending to referring provider<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Click the hamburger menu (3 dashes) → select Chart Export.</li><li>Click the + button next to Admin Documents.</li><li>Find the "Request for Additional Information" doc and move it to the right-hand side.</li><li>Click Export Chart Sections → send to <CopyableContent text={referringProviderName} /></li></ul></li><li>c. Fill out export details<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Type <CopyableContent text={referringProviderName} /></li><li>Confirm method is set to Athena Fax and the fax number has populated.</li><li>Attention box: <CopyableContent text={referringProviderName} /></li><li>Note field: "Request for missing information."</li><li>Confirm Submit, then Save.</li></ul></li><li>d. Add Note to Patient Chart<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>"Valerie Health: Missing Information Requested <CopyableContent text={dateReceived} />"</li></ul></li></ul></li></ol>;
    $[459] = dateReceived;
    $[460] = hasMissingInformation;
    $[461] = referringProviderName;
    $[462] = t51;
  } else {
    t51 = $[462];
  }
  let t52;
  if ($[463] !== t48 || $[464] !== t50 || $[465] !== t51) {
    t52 = <div>{t45}{t46}{t48}{t49}{t50}{t51}</div>;
    $[463] = t48;
    $[464] = t50;
    $[465] = t51;
    $[466] = t52;
  } else {
    t52 = $[466];
  }
  let t53;
  if ($[467] !== t17 || $[468] !== t18 || $[469] !== t19 || $[470] !== t20 || $[471] !== t21 || $[472] !== t22 || $[473] !== t23 || $[474] !== t40 || $[475] !== t44 || $[476] !== t52) {
    t53 = <div className={t17}>{t18}{t19}{t20}{t21}{t22}{t23}{t40}{t44}{t52}</div>;
    $[467] = t17;
    $[468] = t18;
    $[469] = t19;
    $[470] = t20;
    $[471] = t21;
    $[472] = t22;
    $[473] = t23;
    $[474] = t40;
    $[475] = t44;
    $[476] = t52;
    $[477] = t53;
  } else {
    t53 = $[477];
  }
  let t54;
  if ($[478] !== T0 || $[479] !== t24 || $[480] !== t53) {
    t54 = <T0 collapsibleTitle={t24}>{t53}</T0>;
    $[478] = T0;
    $[479] = t24;
    $[480] = t53;
    $[481] = t54;
  } else {
    t54 = $[481];
  }
  let t55;
  if ($[482] !== athenaData || $[483] !== referral || $[484] !== task.id) {
    t55 = <AthenaReferralCards referral={referral} athenaData={athenaData} taskId={task.id} />;
    $[482] = athenaData;
    $[483] = referral;
    $[484] = task.id;
    $[485] = t55;
  } else {
    t55 = $[485];
  }
  let t56;
  if ($[486] !== t25 || $[487] !== t54 || $[488] !== t55) {
    t56 = <div className={t25}>{t54}{t55}</div>;
    $[486] = t25;
    $[487] = t54;
    $[488] = t55;
    $[489] = t56;
  } else {
    t56 = $[489];
  }
  return t56;
}
function _temp45(entry_2) {
  return <li key={entry_2.label}>{entry_2.label}: <CopyableContent text={entry_2.value} /> ({entry_2.source})</li>;
}
function _temp44(entry_1) {
  return entry_1.label;
}
function _temp43(t0) {
  const {
    label,
    resultDocumentLabel
  } = t0;
  return <li key={label}><CopyableContent text={label} />{resultDocumentLabel ? <>{" "}— Add document label: <CopyableContent text={resultDocumentLabel} /></> : null}</li>;
}
function _temp42(entry_0, index_3) {
  return <li key={`${entry_0.label}-${index_3}`}>{entry_0.label} <strong>{entry_0.outcome}</strong></li>;
}
function _temp41(flag_0, index_2) {
  return <li key={index_2}>{flag_0}</li>;
}
function _temp40(appointment) {
  return <li key={appointment.id}>{formatAppointmentHistoryEntry(appointment)}</li>;
}
function _temp39(entry, index_1) {
  return <li key={index_1}>{entry}</li>;
}
function _temp38(flag, index_0) {
  return <li key={index_0}>{flag}</li>;
}
function _temp37(coverage_0) {
  return coverage_0.order === 2;
}
function _temp36(coverage) {
  return coverage.order === 1;
}
function _temp35(a_0, b_0) {
  return (a_0.order ?? 99) - (b_0.order ?? 99);
}
function _temp34(value_0, index, values) {
  return values.indexOf(value_0) === index;
}
function _temp33(value) {
  return Boolean(value);
}
function _temp32(item) {
  return item.diagnosisText;
}
function _temp31(a, b) {
  return (a.order ?? 99) - (b.order ?? 99);
}